'use client'

import ConfirmModal from '@/components/confirm-modal'
import { formatDateOnly } from '@/lib/utils/date-only'
import { messageDayLabel, startsNewDay } from '@/lib/utils/message-day'
import { registerUnsavedWork } from '@/lib/app-version/unsaved-work'
import {
    assignChatUnit,
    markChatRead,
    CHAT_READ_CHANGED_EVENT,
    ChatPeriod,
    ChatWithItems,
    closeChat,
    getChatPeriods,
    getChatWithMessages,
    getOrganizationUnits,
    moveChatDepartment,
    OrganizationUnit,
    reopenChat,
    deleteChatMessage,
    sendManualMessage,
    sendManualTemplate,
    toggleAiOnHold,
    uploadMedia,
} from '@/lib/api/atendimento/chat'
import { getDepartmentsByOrganization } from '@/lib/api/atendimento/departments'
import StartChatModal from './start-chat-modal'
import ChatContactLabel from '@/components/atendimento/ChatContactLabel'
import ChatReaders from '@/components/atendimento/chat-readers'
import {
    getSendableWhatsappTemplates,
    WhatsappTemplate,
} from '@/lib/api/messaging/whatsapp-templates-api'
import { MessageItem } from '@/lib/api/common/interfaces/message.interface'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useChatDockOptional } from '@/lib/providers/chat-dock-provider'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useAutoResizeTextarea } from '@/lib/hooks/use-auto-resize-textarea'
import FilePreviewModal, {
    canPreview,
    type PreviewFile,
} from '@/components/file-preview'
import {
    QuickMessage,
    getAvailableQuickMessages,
    renderQuickMessage,
} from '@/lib/api/messaging/quick-messages-api'
import QuickMessagePicker from './quick-message-picker'
import ChatFlowStrip from './chat-flow-strip'
import ScheduleMessageModal from './schedule-message-modal'
import AiInstructionsModal, {
    describeAiInstructionsValidity,
    getActiveAiInstructions,
} from './ai-instructions-modal'
import {
    getChatFlowRuns,
    getLaunchableSalesFlows,
    startSalesFlowRun,
    type ChatFlowRun,
    type LaunchableSalesFlow,
} from '@/lib/api/sales/flows-api'
import { useAuth } from '@/store/hooks'
import {
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { HiSparkles } from 'react-icons/hi2'
import {
    MdAccessTime,
    MdAdd,
    MdArrowBack,
    MdAttachFile,
    MdBolt,
    MdFullscreen,
    MdBusiness,
    MdChat,
    MdCheck,
    MdCheckCircle,
    MdClose,
    MdAccountBalanceWallet,
    MdExpandMore,
    MdLocationOn,
    MdDelete,
    MdDeleteOutline,
    MdReply,
    MdDownload,
    MdImage,
    MdInsertDriveFile,
    MdMenu,
    MdMic,
    MdPause,
    MdLink,
    MdPerson,
    MdPersonAdd,
    MdPriorityHigh,
    MdPerson2,
    MdPlayArrow,
    MdSend,
    MdTimer,
    MdVideocam,
    MdAccountTree,
    MdScheduleSend,
    MdPsychology,
    MdSmartToy,
    MdKeyboardArrowDown,
} from 'react-icons/md'
import EmojiPickerButton from '@/components/atendimento/emoji-picker-button'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
    ChatFolder,
    getChatFolders,
    getChatFolderForChat,
} from '@/lib/api/atendimento/chat-folders'
import ChatActionsMenu from '@/components/chat-actions-menu'
import ActionsMenu, { ActionMenuItem } from '@/components/actions-menu'
import { WalletAssignModal } from '@/components/wallet/wallet-assign-modal'
import {
    ChatClientCandidate,
    getChatClientCandidates,
    linkChatClient,
    setChatContactPhone,
} from '@/lib/api/atendimento/chat'
import { createClient } from '@/lib/api/clients/client-api'
import { toast } from 'react-toastify'
import {
    emitScreenEvent,
    registerScreenController,
    typeProgressively,
} from '@/lib/ai-user/screen-bridge'

function formatLongSummary(text: string) {
    if (!text) return null

    const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim())

    return paragraphs.map((paragraph, index) => {
        const trimmed = paragraph.trim()

        if (
            trimmed.startsWith('-') ||
            trimmed.startsWith('•') ||
            trimmed.match(/^\d+[\.\)]\s/)
        ) {
            const items = trimmed
                .split('\n')
                .filter(item => item.trim())
                .map(item =>
                    item
                        .replace(/^[-•]\s*/, '')
                        .replace(/^\d+[\.\)]\s*/, '')
                        .trim()
                )

            return (
                <ul
                    key={index}
                    className="list-disc list-inside space-y-2 ml-4 mb-4"
                >
                    {items.map((item, itemIndex) => {
                        const parts: (string | React.ReactElement)[] = []
                        let lastIndex = 0
                        const boldRegex = /\*\*(.+?)\*\*/g
                        let match

                        while ((match = boldRegex.exec(item)) !== null) {
                            if (match.index > lastIndex) {
                                parts.push(
                                    item.substring(lastIndex, match.index)
                                )
                            }
                            parts.push(
                                <strong
                                    key={match.index}
                                    className="font-semibold"
                                >
                                    {match[1]}
                                </strong>
                            )
                            lastIndex = match.index + match[0].length
                        }

                        if (lastIndex < item.length) {
                            parts.push(item.substring(lastIndex))
                        }

                        return (
                            <li
                                key={itemIndex}
                                className="text-base-content/80 leading-6"
                            >
                                {parts.length > 0 ? parts : item}
                            </li>
                        )
                    })}
                </ul>
            )
        }

        if (
            trimmed.length < 120 &&
            (trimmed.endsWith(':') ||
                (!trimmed.includes('.') &&
                    trimmed.length > 10 &&
                    trimmed.length < 80))
        ) {
            const parts: (string | React.ReactElement)[] = []
            let lastIndex = 0
            const boldRegex = /\*\*(.+?)\*\*/g
            let match

            while ((match = boldRegex.exec(trimmed)) !== null) {
                if (match.index > lastIndex) {
                    parts.push(trimmed.substring(lastIndex, match.index))
                }
                parts.push(
                    <strong key={match.index} className="font-semibold">
                        {match[1]}
                    </strong>
                )
                lastIndex = match.index + match[0].length
            }

            if (lastIndex < trimmed.length) {
                parts.push(trimmed.substring(lastIndex))
            }

            return (
                <h3
                    key={index}
                    className="text-base font-semibold mt-6 mb-3 first:mt-0"
                >
                    {parts.length > 0 ? parts : trimmed.replace(/:$/, '')}
                </h3>
            )
        }

        const parts: (string | React.ReactElement)[] = []
        let lastIndex = 0
        const boldRegex = /\*\*(.+?)\*\*/g
        let match

        while ((match = boldRegex.exec(trimmed)) !== null) {
            if (match.index > lastIndex) {
                parts.push(trimmed.substring(lastIndex, match.index))
            }
            parts.push(
                <strong key={match.index} className="font-semibold">
                    {match[1]}
                </strong>
            )
            lastIndex = match.index + match[0].length
        }

        if (lastIndex < trimmed.length) {
            parts.push(trimmed.substring(lastIndex))
        }

        return (
            <p
                key={index}
                className="text-base-content/80 leading-7 mb-4 last:mb-0"
            >
                {parts.length > 0 ? parts : trimmed}
            </p>
        )
    })
}

const FILE_URL_REGEX = /\[Arquivo enviado:\s*(https?:\/\/[^\s\]]+)\]/g
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif']
const AUDIO_EXTENSIONS = ['.ogg', '.mp3', '.wav', '.m4a', '.opus']
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.webm']

/**
 * Extensão do arquivo, olhando SÓ o último segmento do caminho.
 *
 * O `split('.').pop()` sobre a URL inteira olhava o ponto do DOMÍNIO quando o arquivo
 * não tinha extensão: `https://storage.mediardex.com/messages/2026/09/<uuid>` devolvia
 * `com/messages/2026/09/<uuid>` como "extensão" — que nunca casa com lista nenhuma, e
 * ainda era exibido no card como se fosse o tipo do arquivo.
 */
function getFileExtension(url: string): string {
    const lastSegment = url.split('?')[0].split('#')[0].split('/').pop() ?? ''
    const dot = lastSegment.lastIndexOf('.')
    if (dot <= 0) return ''
    return lastSegment.slice(dot + 1).toLowerCase()
}

/**
 * Rótulos que o parser do WhatsApp põe no corpo da mensagem
 * (`baileys-message-parser.ts`) quando a mídia não tem legenda.
 *
 * São o ÚNICO indício de tipo nas mensagens antigas: até 02/09/2026 o áudio subia ao
 * storage sem extensão (o sniff não conhecia a assinatura `OggS` e o mime vinha com
 * `; codecs=opus`), e não há mimeType gravado na mensagem. São ~926 áudios em produção
 * que a extensão sozinha nunca vai reconhecer — este mapa é o que faz o player aparecer
 * neles sem precisar reescrever o histórico.
 */
const CONTENT_LABEL_KINDS: { label: string; kind: 'audio' | 'image' | 'video' }[] = [
    { label: '[Áudio]', kind: 'audio' },
    { label: '[Imagem]', kind: 'image' },
    { label: '[Vídeo]', kind: 'video' },
]

function getKindFromContentLabel(
    content: string,
): 'audio' | 'image' | 'video' | null {
    const found = CONTENT_LABEL_KINDS.find(({ label }) => content.includes(label))
    return found?.kind ?? null
}

function getFileName(content: string, url: string): string {
    const lines = content.split('\n')
    const nameCandidate = lines.find(l => !l.startsWith('[') && l.trim())
    if (nameCandidate?.trim()) return nameCandidate.trim()
    return url.split('/').pop()?.split('?')[0] || 'arquivo'
}

// Converte a formatação de texto do WhatsApp para HTML seguro.
// Regras: *negrito*  _itálico_  ~tachado~  ```mono```  `mono`
// Escapa o HTML ANTES de aplicar o markup — só injetamos tags conhecidas.
function formatWhatsAppHtml(text: string): string {
    let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

    html = html
        // monospace em bloco (```...```), inclusive multilinha
        .replace(/```([\s\S]+?)```/g, '<code>$1</code>')
        // monospace inline (`...`)
        .replace(/`([^`\n]+)`/g, '<code>$1</code>')
        // negrito *...*  (não inicia/termina em espaço ou asterisco)
        .replace(/\*([^\s*][^*\n]*?[^\s*]|[^\s*])\*/g, '<strong>$1</strong>')
        // itálico _..._
        .replace(/_([^\s_][^_\n]*?[^\s_]|[^\s_])_/g, '<em>$1</em>')
        // tachado ~...~
        .replace(/~([^\s~][^~\n]*?[^\s~]|[^\s~])~/g, '<del>$1</del>')

    return html
}

// Span com o texto já formatado no estilo WhatsApp, preservando quebras/espaços.
function FormattedText({ text }: { text: string }) {
    return (
        <span
            className="whitespace-pre-wrap break-words"
            dangerouslySetInnerHTML={{ __html: formatWhatsAppHtml(text) }}
        />
    )
}

function renderMessageContent(
    content: string,
    isClient: boolean,
    onOpenMedia: (media: PreviewFile) => void
) {
    const urls: { url: string; fullMatch: string }[] = []
    let match: RegExpExecArray | null
    const regex = new RegExp(FILE_URL_REGEX.source, 'g')
    while ((match = regex.exec(content)) !== null) {
        urls.push({ url: match[1], fullMatch: match[0] })
    }

    if (urls.length === 0) return <FormattedText text={content} />

    let textContent = content
    for (const { fullMatch } of urls) {
        textContent = textContent.replace(fullMatch, '').trim()
    }

    // Remover nome do arquivo duplicado se for a primeira linha
    const lines = textContent.split('\n').filter(l => l.trim())
    if (lines.length === 1 && urls.length === 1) {
        const ext = getFileExtension(urls[0].url)
        if (lines[0].trim().endsWith(`.${ext}`)) {
            textContent = ''
        }
    }

    return (
        <>
            {textContent && <FormattedText text={textContent} />}
            {urls.map(({ url }, i) => {
                const ext = getFileExtension(url)
                const fileName = getFileName(content, url)

                // Sem extensão reconhecível, o rótulo do corpo ([Áudio], [Imagem],
                // [Vídeo]) decide. É o que salva as mensagens antigas, gravadas quando
                // o áudio subia sem extensão.
                const labelKind =
                    urls.length === 1 ? getKindFromContentLabel(content) : null

                if (
                    IMAGE_EXTENSIONS.includes(`.${ext}`) ||
                    (!ext && labelKind === 'image')
                ) {
                    return (
                        <button
                            key={i}
                            type="button"
                            className="block mt-2 cursor-zoom-in"
                            onClick={() =>
                                onOpenMedia({ url, kind: 'image', fileName })
                            }
                            title="Ampliar"
                        >
                            <img
                                src={url}
                                alt={fileName}
                                className="max-w-[280px] rounded-lg"
                            />
                        </button>
                    )
                }

                if (
                    AUDIO_EXTENSIONS.includes(`.${ext}`) ||
                    (!ext && labelKind === 'audio')
                ) {
                    return (
                        <audio
                            key={i}
                            controls
                            className="mt-2 max-w-full"
                            preload="none"
                        >
                            <source src={url} />
                        </audio>
                    )
                }

                if (
                    VIDEO_EXTENSIONS.includes(`.${ext}`) ||
                    (!ext && labelKind === 'video')
                ) {
                    // O player inline continua: dar play sem sair da conversa é
                    // o caso comum. O botão amplia para tela cheia quando o
                    // quadro pequeno não basta.
                    return (
                        <div
                            key={i}
                            className="relative mt-2 w-fit group/video"
                        >
                            <video
                                controls
                                className="max-w-[320px] rounded-lg block"
                                preload="none"
                            >
                                <source src={url} />
                            </video>
                            <button
                                type="button"
                                className="absolute top-1 right-1 btn btn-xs btn-square bg-black/60 border-none text-white hover:bg-black/80 opacity-0 group-hover/video:opacity-100 transition-opacity"
                                onClick={() =>
                                    onOpenMedia({
                                        url,
                                        kind: 'video',
                                        fileName,
                                    })
                                }
                                title="Ampliar"
                            >
                                <MdFullscreen size={16} />
                            </button>
                        </div>
                    )
                }

                const attachmentClass = `flex items-center gap-2 mt-2 rounded-lg p-3 transition-colors w-full text-left ${
                    isClient
                        ? 'bg-base-200/50 hover:bg-base-200'
                        : 'bg-white/10 hover:bg-white/20'
                }`
                const attachmentBody = (
                    <>
                        <MdInsertDriveFile className="text-2xl shrink-0" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">
                                {fileName}
                            </p>
                            {ext && (
                                <p className="text-xs opacity-60">
                                    .{ext.toUpperCase()}
                                </p>
                            )}
                        </div>
                        <MdDownload className="text-lg shrink-0" />
                    </>
                )

                // PDF e texto abrem no visualizador, junto da conversa. Formato
                // sem visualização continua indo para nova aba, onde o
                // navegador ao menos oferece o download.
                if (canPreview(fileName)) {
                    return (
                        <button
                            key={i}
                            type="button"
                            className={attachmentClass}
                            onClick={() => onOpenMedia({ url, fileName })}
                            title="Visualizar"
                        >
                            {attachmentBody}
                        </button>
                    )
                }

                return (
                    <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={attachmentClass}
                    >
                        {attachmentBody}
                    </a>
                )
            })}
        </>
    )
}

interface AtendimentoChatPageProps {
    chatIdOverride?: number
    onBack?: () => void
}

export default function AtendimentoChatPage({
    chatIdOverride,
    onBack,
}: AtendimentoChatPageProps = {}) {
    const { id: paramId } = useParams<{ id: string }>()
    const chatId = chatIdOverride ? String(chatIdOverride) : paramId
    const id = chatId
    const navigate = useNavigate()
    const chatDock = useChatDockOptional()

    /**
     * Sair da conversa para outra tela.
     *
     * No dock, a conversa é uma sobreposição `fixed` por cima de tudo: navegar sem
     * fechá-la carrega a tela nova ATRÁS dela, e a pessoa fica olhando a mesma
     * conversa achando que o clique não funcionou. Só fecha quando a sobreposição é
     * desta conversa — no popup e nas abas do negócio e do lead, o dock não tem nada
     * a ver com o clique.
     *
     * `remember: false` porque fechar de passagem não é decidir trabalhar sem o dock:
     * ele volta aberto na próxima tela.
     */
    const navigateOutOfDock = (path: string) => {
        if (
            chatDock?.isDockOpen &&
            chatDock.openChatId != null &&
            String(chatDock.openChatId) === chatId
        ) {
            chatDock.closeDock({ remember: false })
        }
        navigate(path)
    }
    const location = useLocation()
    const { user } = useAuth()
    const { socket, isConnected } = useContext(SocketContext)
    const { clientLabel } = useClientLabel()

    const clientId = (location.state as any)?.clientId

    // Atribuição de carteira a partir do chat: o cliente da conversa é movido
    // para a carteira escolhida, sem sair da tela.
    const [showWalletModal, setShowWalletModal] = useState(false)

    // Desambiguação: telefone é 1:N, então quando mais de um cliente casa o
    // atendente é quem diz quem é — o sistema não escolhe.
    const [showLinkModal, setShowLinkModal] = useState(false)
    const [candidates, setCandidates] = useState<ChatClientCandidate[]>([])
    const [loadingCandidates, setLoadingCandidates] = useState(false)
    const [linkingClientId, setLinkingClientId] = useState<number | null>(null)
    // Cadastro do novo cliente sem sair da conversa: mandar o atendente para a
    // tela de clientes fazia ele perder o contexto e ter que voltar para
    // vincular. Aqui o cadastro nasce já com o telefone e já é vinculado.
    const [creatingNewClient, setCreatingNewClient] = useState(false)
    const [newClientName, setNewClientName] = useState('')
    const [newClientCpf, setNewClientCpf] = useState('')
    const [savingNewClient, setSavingNewClient] = useState(false)
    const [contactPhoneInput, setContactPhoneInput] = useState('')
    const [savingContactPhone, setSavingContactPhone] = useState(false)

    const [loading, setLoading] = useState(true)
    const [atendimento, setAtendimento] = useState<ChatWithItems | null>(null)
    // Resumos por período do atendimento (chat reusado acumula vários).
    const [summaries, setSummaries] = useState<ChatPeriod[]>([])
    const [messages, setMessages] = useState<MessageItem[]>([])
    const [replyTo, setReplyTo] = useState<MessageItem | null>(null)
    const [messageToDelete, setMessageToDelete] = useState<MessageItem | null>(
        null
    )
    const [deletingMessage, setDeletingMessage] = useState(false)
    const [inputText, setInputText] = useState('')

    // Mensagem digitada e ainda não enviada é a coisa que o recarregamento
    // automático pós-deploy não pode destruir: com texto aqui, o sistema avisa e
    // espera em vez de recarregar por cima. Ver `lib/app-version/`.
    useEffect(
        () => registerUnsavedWork('atendimento:composer', () => inputText.trim().length > 0),
        [inputText],
    )
    // O campo nasce com 3 linhas e acompanha o texto até 240px — acima disso
    // rola, para o input não engolir a conversa.
    const { ref: inputRef } = useAutoResizeTextarea(inputText, {
        minHeight: 96,
        maxHeight: 240,
    })
    const [sending, setSending] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [uploadedUrls, setUploadedUrls] = useState<string[]>([])
    // Imagem/vídeo abertos em tela cheia. Nova aba tirava o operador da
    // conversa; PDF e documento seguem no visualizador do navegador.
    const [viewingMedia, setViewingMedia] = useState<PreviewFile | null>(null)
    // Mensagens rápidas ("/"): a lista disponível é carregada uma vez por chat,
    // no primeiro uso do atalho.
    const [quickMessages, setQuickMessages] = useState<QuickMessage[]>([])
    const [quickLoaded, setQuickLoaded] = useState(false)
    const [quickLoading, setQuickLoading] = useState(false)
    const [quickOpen, setQuickOpen] = useState(false)
    const [quickQuery, setQuickQuery] = useState('')
    const [quickIndex, setQuickIndex] = useState(0)
    // Anexos que vieram da mensagem rápida escolhida: já estão no storage, então
    // não passam pelo upload — entram direto no envio.
    const [quickMediaUrls, setQuickMediaUrls] = useState<string[]>([])
    // Lead da conversa: é para ele que se inicia fluxo e se agenda mensagem.
    const chatLeadId: number | null = (atendimento as any)?.leadId ?? null
    const currentWalletId: number | null =
        (atendimento as any)?.client?.walletId ?? null
    const currentWalletName: string | null =
        (atendimento as any)?.client?.wallet?.name ?? null
    // Fluxos no "/" (docs/sales-flows.md, seção 6): escolher um NÃO inicia na hora,
    // vira um chip que o atendente confirma.
    const [launchableFlows, setLaunchableFlows] = useState<LaunchableSalesFlow[]>([])
    const [pendingFlow, setPendingFlow] = useState<LaunchableSalesFlow | null>(null)
    const [startingFlow, setStartingFlow] = useState(false)
    // Faixa: fluxos em andamento e mensagens agendadas do lead desta conversa.
    const [flowRuns, setFlowRuns] = useState<ChatFlowRun[]>([])
    const [showScheduleModal, setShowScheduleModal] = useState(false)
    const [showAiInstructionsModal, setShowAiInstructionsModal] = useState(false)
    const [showCloseModal, setShowCloseModal] = useState(false)
    const [closing, setClosing] = useState(false)
    const [viewingMessages, setViewingMessages] = useState(false) // Para visualizar mensagens quando fechado
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [attachMenuOpen, setAttachMenuOpen] = useState(false)
    const [chatFolders, setChatFolders] = useState<ChatFolder[]>([])
    const [currentFolderId, setCurrentFolderId] = useState<number | null>(null)
    // Unidade (cidade) do atendimento + dropdown de definição em triagem.
    const [showUnitMenu, setShowUnitMenu] = useState(false)
    const [units, setUnits] = useState<OrganizationUnit[]>([])
    const [loadingUnits, setLoadingUnits] = useState(false)
    const [assigningUnit, setAssigningUnit] = useState(false)
    // Gravação de áudio/vídeo (mensagem de voz/clipe). O arquivo gravado
    // (webm) entra em selectedFiles e o backend transcodifica p/ ogg/mp4.
    const [recording, setRecording] = useState<null | 'audio' | 'video'>(null)
    const [recordingTime, setRecordingTime] = useState(0)
    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const recordedChunksRef = useRef<Blob[]>([])
    const recordingStreamRef = useRef<MediaStream | null>(null)
    const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(
        null
    )
    const recordingCanceledRef = useRef(false)
    const videoPreviewRef = useRef<HTMLVideoElement | null>(null)

    const loadChatFolders = useCallback(async () => {
        if (!id) return
        const [foldersRes, currentRes] = await Promise.all([
            getChatFolders(),
            getChatFolderForChat(Number(id)),
        ])
        if (foldersRes.success && Array.isArray(foldersRes.result)) {
            setChatFolders(foldersRes.result)
        }
        if (currentRes.success) {
            setCurrentFolderId(currentRes.result?.folderId ?? null)
        }
    }, [id])

    useEffect(() => {
        loadChatFolders()
    }, [loadChatFolders])

    // Envio de template (permitido fora da janela de 24h)
    const [showTemplateModal, setShowTemplateModal] = useState(false)
    const [sendableTemplates, setSendableTemplates] = useState<
        WhatsappTemplate[]
    >([])
    const [loadingTemplates, setLoadingTemplates] = useState(false)
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
        null
    )
    const [templateVars, setTemplateVars] = useState<Record<string, string>>({})
    const [sendingTemplate, setSendingTemplate] = useState(false)

    // Revisão antes de enviar
    const [reviewMode, setReviewMode] = useState(() => {
        const saved = localStorage.getItem('chatReviewMode')
        return saved ? JSON.parse(saved) : false
    })
    const [pendingMessages, setPendingMessages] = useState<
        {
            id: string
            text: string
            mediaUrls: string[]
            files: File[]
            remainingSeconds: number
            paused: boolean
        }[]
    >([])
    const pendingTimersRef = useRef<
        Map<string, ReturnType<typeof setInterval>>
    >(new Map())

    const messagesEndRef = useRef<HTMLDivElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Reabertura por template (fora da janela) e mover de departamento.
    const [showReopenModal, setShowReopenModal] = useState(false)
    const [moveDepartments, setMoveDepartments] = useState<
        { id: number; name: string }[]
    >([])
    const [movingDept, setMovingDept] = useState(false)
    const [showDeptMenu, setShowDeptMenu] = useState(false)

    // Verificar se está dentro da janela de 23h (WhatsApp permite 24h, 1h de margem de segurança)
    const isWithinWhatsAppWindow = (): boolean => {
        if (!messages.length) return false

        // Buscar a última mensagem do cliente (user)
        const lastClientMessage = [...messages]
            .reverse()
            .find(msg => msg.role === 'user')

        if (!lastClientMessage) return false

        const lastMessageTime = new Date(lastClientMessage.createdAt).getTime()
        const now = new Date().getTime()
        const hoursSinceLastMessage = (now - lastMessageTime) / (1000 * 60 * 60)

        return hoursSinceLastMessage < 23
    }

    /**
     * Quem decide se dá para escrever é o BACKEND: `whatsappWindowOpen` já leva
     * em conta o canal do setor — no WhatsApp Web não existe janela de 24h, e
     * exigir template ali seria inventar uma restrição que o canal não tem.
     *
     * O cálculo local só vale como ponte até o atendimento carregar; usá-lo como
     * fonte (era o que acontecia) mantinha o input bloqueado mesmo no canal sem
     * janela, porque ele só olha o horário da última mensagem do cliente.
     */
    const canInteract =
        atendimento?.whatsappWindowOpen ?? isWithinWhatsAppWindow()

    // Departamentos da organização (para mover o atendimento de setor).
    useEffect(() => {
        const orgId = user?.organizationId ?? user?.company?.organizationId
        if (!orgId) return
        getDepartmentsByOrganization(orgId)
            .then(res => {
                if (res.success && Array.isArray(res.result)) {
                    setMoveDepartments(
                        res.result.map((d: any) => ({
                            id: d.id,
                            name: d.name,
                        }))
                    )
                }
            })
            .catch(() => {})
    }, [user?.organizationId, user?.company?.organizationId])

    const pendingMessagesRef = useRef(pendingMessages)
    pendingMessagesRef.current = pendingMessages

    // Cleanup timers ao desmontar + ESC para cancelar
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && pendingMessagesRef.current.length > 0) {
                const lastPending =
                    pendingMessagesRef.current[
                        pendingMessagesRef.current.length - 1
                    ]
                cancelPendingMessage(lastPending.id)
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            pendingTimersRef.current.forEach(timer => clearInterval(timer))
            pendingTimersRef.current.clear()
        }
    }, [])

    const toggleReviewMode = (enabled: boolean) => {
        setReviewMode(enabled)
        localStorage.setItem('chatReviewMode', JSON.stringify(enabled))
    }

    const addPendingMessage = (
        text: string,
        mediaUrls: string[],
        files: File[]
    ) => {
        const msgId = `pending-${Date.now()}`
        const newPending = {
            id: msgId,
            text,
            mediaUrls,
            files,
            remainingSeconds: 10,
            paused: false,
        }
        setPendingMessages(prev => [...prev, newPending])

        const timer = setInterval(() => {
            setPendingMessages(prev => {
                const msg = prev.find(m => m.id === msgId)
                if (!msg) {
                    clearInterval(timer)
                    pendingTimersRef.current.delete(msgId)
                    return prev
                }
                if (msg.paused) return prev
                if (msg.remainingSeconds <= 1) {
                    clearInterval(timer)
                    pendingTimersRef.current.delete(msgId)
                    sendPendingMessage(msg.text, msg.mediaUrls)
                    return prev.filter(m => m.id !== msgId)
                }
                return prev.map(m =>
                    m.id === msgId
                        ? { ...m, remainingSeconds: m.remainingSeconds - 1 }
                        : m
                )
            })
        }, 1000)

        pendingTimersRef.current.set(msgId, timer)
        scrollToBottom()
    }

    const pausePendingMessage = (msgId: string) => {
        setPendingMessages(prev =>
            prev.map(m => (m.id === msgId ? { ...m, paused: true } : m))
        )
    }

    const resumePendingMessage = (msgId: string) => {
        setPendingMessages(prev =>
            prev.map(m => (m.id === msgId ? { ...m, paused: false } : m))
        )
    }

    const cancelPendingMessage = (msgId: string) => {
        const timer = pendingTimersRef.current.get(msgId)
        if (timer) {
            clearInterval(timer)
            pendingTimersRef.current.delete(msgId)
        }
        setPendingMessages(prev => prev.filter(m => m.id !== msgId))
        toast.info('Mensagem cancelada')
    }

    const sendPendingMessage = async (text: string, mediaUrls: string[]) => {
        if (!atendimento) return
        try {
            const response = await sendManualMessage({
                messageId: atendimento.id,
                body: text || undefined,
                mediaUrl: mediaUrls.length > 0 ? mediaUrls : undefined,
            })
            if (response.success) {
                await loadAtendimento(true)
            } else {
                toast.error(response.error || 'Erro ao enviar mensagem')
            }
        } catch (error: any) {
            toast.error(error.message || 'Erro ao enviar mensagem')
        }
    }

    useEffect(() => {
        if (id) {
            loadAtendimento()
            loadSummaries()
        }
    }, [id])

    useEffect(() => {
        scrollToBottom()
    }, [messages])

    // Resetar visualização de mensagens quando atendimento mudar de status
    useEffect(() => {
        if (atendimento?.status === 'OPEN') {
            setViewingMessages(false)
        }
    }, [atendimento?.status])

    // Fechar menu de anexos ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                attachMenuOpen &&
                !(event.target as Element).closest('.attach-menu-container')
            ) {
                setAttachMenuOpen(false)
            }
        }

        if (attachMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
            }
        }
    }, [attachMenuOpen])

    // Fechar menu de unidade ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                showUnitMenu &&
                !(event.target as Element).closest('.unit-menu-container')
            ) {
                setShowUnitMenu(false)
            }
        }

        if (showUnitMenu) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
            }
        }
    }, [showUnitMenu])

    // Não lidas: a conversa aberta e visível conta como vista pela equipe. Marca ao abrir,
    // ao voltar para a aba e a cada mensagem do contato (agrupando rajadas).
    const readTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const scheduleMarkRead = useCallback((chatId: number) => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
        if (readTimer.current) clearTimeout(readTimer.current)
        readTimer.current = setTimeout(() => {
            markChatRead(chatId)
                .then(res => {
                    if (res.success) {
                        window.dispatchEvent(
                            new CustomEvent(CHAT_READ_CHANGED_EVENT, { detail: { chatId } }),
                        )
                    }
                })
                .catch(() => {})
        }, 800)
    }, [])

    useEffect(() => {
        if (!atendimento?.id) return
        scheduleMarkRead(atendimento.id)
        const onVisible = () => {
            if (document.visibilityState === 'visible') scheduleMarkRead(atendimento.id)
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => {
            document.removeEventListener('visibilitychange', onVisible)
            if (readTimer.current) clearTimeout(readTimer.current)
        }
    }, [atendimento?.id, scheduleMarkRead])

    // Socket listener for new messages
    useEffect(() => {
        if (!socket || !isConnected || !atendimento) return

        const handleNewMessage = (data: any) => {
            console.log('[Chat] New message received:', data)

            if (data.messageItem) {
                // Adicionar nova mensagem em tempo real
                setMessages(prev => {
                    const exists = prev.some(
                        msg => msg.id === data.messageItem.id
                    )
                    if (exists) return prev

                    // O som de mensagem nova é do "Meus Alertas" (navbar), que toca em
                    // qualquer tela; tocar aqui também faria soar duas vezes.
                    if (data.messageItem.role === 'user') scheduleMarkRead(atendimento.id)

                    return [...prev, data.messageItem]
                })
            } else {
                // Fallback: recarregar mensagens em segundo plano (sem spinner)
                loadAtendimento(true)
            }
        }

        const handleMessageUpdate = async (data: any) => {
            console.log('[Chat] Message updated:', data)

            // Refresh silencioso (sem trocar a tela pelo spinner)
            await loadAtendimento(true)
        }

        const handleMessageClosed = async (data: any) => {
            console.log('[Chat] Message closed event received:', data)

            if (data.messageId === atendimento.id) {
                await loadAtendimento(true)

                toast.info('Atendimento foi fechado')
            }
        }

        const handleSummaryGenerated = async (data: any) => {
            console.log('[Chat] Summary generated event received:', data)

            if (data.chatId === atendimento.id) {
                await Promise.all([loadAtendimento(true), loadSummaries()])

                // skipped = período sem mensagens suficientes; não houve resumo.
                if (!data.skipped) {
                    toast.success('Resumo do atendimento gerado com sucesso!')
                }
            }
        }

        // Mensagem apagada por outro operador: some da lista sem recarregar.
        const handleMessageDeleted = (data: any) => {
            setMessages(prev => prev.filter(m => m.id !== data.messageId))
        }

        /**
         * A conversa mudou de dono enquanto estava aberta.
         *
         * Não decide no cliente se a pessoa perdeu o acesso — quem sabe é o servidor:
         * refaz a busca e, se ela não vier mais, sai da tela. Adivinhar aqui erraria
         * nos casos legítimos (quem continua dono do lead, por exemplo, segue vendo a
         * conversa e não deve ser expulso).
         *
         * No dock volta para a lista (`onBack`); na tela cheia navega preservando o
         * recorte, que é o mesmo cuidado de não perder o filtro ao abrir a conversa.
         */
        const handleTransferred = async (data: {
            target?: string
            ownerUserIds?: number[]
        }) => {
            // A pergunta é "isto ainda é MEU?", não "ainda consigo ver?".
            //
            // Conta COMPANY enxerga todas as conversas: pelo critério de acesso ela
            // nunca sairia daquela que acabou de passar adiante. O servidor manda quem
            // responde pela conversa DEPOIS da transferência, e quem não está na lista
            // sai da tela.
            const owners = data?.ownerUserIds ?? []
            const stillMine = !!user?.id && owners.includes(user.id)

            if (stillMine) {
                // Continua sendo dele: só atualiza, porque a carteira do cabeçalho mudou.
                const response = await getChatWithMessages(parseInt(id!))
                if (response.success && response.result) {
                    setAtendimento(response.result)
                    setMessages(response.result.messages || [])
                }
                return
            }

            toast.info(
                data?.target
                    ? `Este atendimento passou para ${data.target}.`
                    : 'Este atendimento passou para outra pessoa.'
            )
            if (onBack) onBack()
            else navigate({ pathname: '/atendimento/chats', search: location.search })
        }

        socket.on(`message-${atendimento.id}`, handleNewMessage)
        socket.on(`message-update-${atendimento.id}`, handleMessageUpdate)
        socket.on(`message-deleted-${atendimento.id}`, handleMessageDeleted)
        socket.on(`summary-generated-${atendimento.id}`, handleSummaryGenerated)
        socket.on(`chat-transferred-${atendimento.id}`, handleTransferred)

        if (user.company?.id) {
            socket.on(`message-closed-${user.company.id}`, handleMessageClosed)
            socket.on(
                `summary-generated-company-${user.company.id}`,
                handleSummaryGenerated
            )
        }

        return () => {
            socket.off(`message-${atendimento.id}`, handleNewMessage)
            socket.off(`chat-transferred-${atendimento.id}`, handleTransferred)
            socket.off(`message-update-${atendimento.id}`, handleMessageUpdate)
            socket.off(
                `message-deleted-${atendimento.id}`,
                handleMessageDeleted
            )
            socket.off(
                `summary-generated-${atendimento.id}`,
                handleSummaryGenerated
            )

            if (user.company?.id) {
                socket.off(
                    `message-closed-${user.company.id}`,
                    handleMessageClosed
                )
                socket.off(
                    `summary-generated-company-${user.company.id}`,
                    handleSummaryGenerated
                )
            }
        }
    }, [socket, isConnected, atendimento, scheduleMarkRead])

    // silent=true: refresh em segundo plano (não troca a tela pelo spinner
    // full — usado após enviar mensagem e em eventos de socket). Só o
    // carregamento inicial usa o spinner para evitar o "reload" da tela toda.
    // Motivo real da falha ao abrir (ex.: "Você não faz parte do setor deste atendimento").
    // Antes a tela mostrava um erro genérico e navegava para Atendimentos — no popup do
    // funil isso tirava a pessoa da tela em que estava.
    const [loadError, setLoadError] = useState<string | null>(null)

    const loadAtendimento = async (silent = false) => {
        try {
            if (!silent) setLoading(true)
            const response = await getChatWithMessages(parseInt(id!))

            if (response.success && response.result) {
                setLoadError(null)
                setAtendimento(response.result)
                setMessages(response.result.messages || [])
            } else if (!silent) {
                const reason = response.error || 'Não foi possível carregar o atendimento'
                setLoadError(reason)
                toast.error(reason)
            }
        } catch (error: any) {
            if (!silent) {
                const reason = error?.message || 'Não foi possível carregar o atendimento'
                setLoadError(reason)
                toast.error(reason)
            }
        } finally {
            if (!silent) setLoading(false)
        }
    }

    const loadSummaries = async () => {
        try {
            const res = await getChatPeriods(parseInt(id!))
            if (res.success && res.result) setSummaries(res.result)
        } catch {
            // best-effort: a seção de resumos só não aparece
        }
    }

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
    }

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || [])
        if (files.length > 0) {
            setSelectedFiles(prev => [...prev, ...files])
        }
    }

    const handleRemoveFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index))
    }

    const handleUploadFiles = async () => {
        if (selectedFiles.length === 0) return []

        try {
            setUploading(true)
            const response = await uploadMedia(selectedFiles)

            if (response.success && response.result) {
                const urls = response.result.files.map(file => file.url)
                setUploadedUrls(urls)
                setSelectedFiles([])
                toast.success('Arquivos enviados com sucesso!')
                return urls
            } else {
                toast.error('Erro ao fazer upload dos arquivos')
                return []
            }
        } catch (error: any) {
            toast.error(error.message || 'Erro ao fazer upload')
            return []
        } finally {
            setUploading(false)
        }
    }

    const formatRecTime = (s: number): string => {
        const mm = String(Math.floor(s / 60)).padStart(2, '0')
        const ss = String(s % 60).padStart(2, '0')
        return `${mm}:${ss}`
    }

    const pickRecorderMime = (kind: 'audio' | 'video'): string => {
        const candidates =
            kind === 'audio'
                ? [
                      'audio/webm;codecs=opus',
                      'audio/ogg;codecs=opus',
                      'audio/webm',
                  ]
                : [
                      'video/webm;codecs=vp9,opus',
                      'video/webm;codecs=vp8,opus',
                      'video/webm',
                      'video/mp4',
                  ]
        for (const c of candidates) {
            if (
                typeof MediaRecorder !== 'undefined' &&
                MediaRecorder.isTypeSupported(c)
            ) {
                return c
            }
        }
        return ''
    }

    const startRecording = async (kind: 'audio' | 'video') => {
        if (recording) return
        try {
            const constraints: MediaStreamConstraints =
                kind === 'audio'
                    ? { audio: true }
                    : { audio: true, video: { facingMode: 'user' } }
            const stream =
                await navigator.mediaDevices.getUserMedia(constraints)
            recordingStreamRef.current = stream
            recordingCanceledRef.current = false
            recordedChunksRef.current = []

            const mimeType = pickRecorderMime(kind)
            const recorder = new MediaRecorder(
                stream,
                mimeType ? { mimeType } : undefined
            )
            mediaRecorderRef.current = recorder

            recorder.ondataavailable = e => {
                if (e.data && e.data.size > 0)
                    recordedChunksRef.current.push(e.data)
            }
            recorder.onstop = () => {
                recordingStreamRef.current?.getTracks().forEach(t => t.stop())
                recordingStreamRef.current = null
                if (recordingTimerRef.current) {
                    clearInterval(recordingTimerRef.current)
                    recordingTimerRef.current = null
                }
                const canceled = recordingCanceledRef.current
                const chunks = recordedChunksRef.current
                recordedChunksRef.current = []
                setRecording(null)
                setRecordingTime(0)
                if (canceled || chunks.length === 0) return

                const type =
                    recorder.mimeType ||
                    (kind === 'audio' ? 'audio/webm' : 'video/webm')
                const baseType = type.split(';')[0]
                const ext = baseType.includes('ogg')
                    ? 'ogg'
                    : baseType.includes('mp4')
                      ? kind === 'audio'
                          ? 'm4a'
                          : 'mp4'
                      : 'webm'
                const blob = new Blob(chunks, { type: baseType })
                const file = new File(
                    [blob],
                    `gravacao-${kind}-${Date.now()}.${ext}`,
                    { type: baseType }
                )
                setSelectedFiles(prev => [...prev, file])
            }

            recorder.start()
            setRecording(kind)
            setRecordingTime(0)
            recordingTimerRef.current = setInterval(
                () => setRecordingTime(t => t + 1),
                1000
            )
        } catch {
            toast.error(
                'Não foi possível acessar o microfone/câmera. Verifique as permissões do navegador.'
            )
            setRecording(null)
        }
    }

    const stopRecording = (cancel = false) => {
        recordingCanceledRef.current = cancel
        const r = mediaRecorderRef.current
        if (r && r.state !== 'inactive') {
            r.stop()
        } else {
            recordingStreamRef.current?.getTracks().forEach(t => t.stop())
            recordingStreamRef.current = null
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current)
                recordingTimerRef.current = null
            }
            setRecording(null)
            setRecordingTime(0)
        }
    }

    // Conecta o preview ao stream quando a gravação de vídeo inicia.
    useEffect(() => {
        if (
            recording === 'video' &&
            videoPreviewRef.current &&
            recordingStreamRef.current
        ) {
            videoPreviewRef.current.srcObject = recordingStreamRef.current
            videoPreviewRef.current.play().catch(() => {})
        }
    }, [recording])

    // Cleanup ao desmontar: encerra stream/timer de gravação.
    useEffect(() => {
        return () => {
            recordingStreamRef.current?.getTracks().forEach(t => t.stop())
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current)
            }
        }
    }, [])

    // Extrai os índices das variáveis posicionais ({{1}}, {{2}}...) da mensagem
    const extractTemplateVars = (body: string): number[] => {
        const matches = Array.from(body.matchAll(/\{\{\s*(\d+)\s*\}\}/g))
        const set = new Set<number>()
        for (const m of matches) set.add(parseInt(m[1], 10))
        return Array.from(set).sort((a, b) => a - b)
    }

    const selectedTemplate = sendableTemplates.find(
        t => t.id === selectedTemplateId
    )

    // Template com variáveis semânticas: o backend resolve tudo no envio
    // (client.name, user.name, ...). O atendente não preenche nada.
    const isTokenizedTemplate =
        !!selectedTemplate?.bodyTemplate ||
        !!selectedTemplate?.variables?.some(v => !!v.token)

    const openTemplateModal = async () => {
        setShowTemplateModal(true)
        setSelectedTemplateId(null)
        setTemplateVars({})
        setLoadingTemplates(true)
        try {
            // O setor decide se a aprovação da Meta é exigida: no WhatsApp Web
            // o corpo vai como texto comum e template em rascunho já é enviável.
            const res = await getSendableWhatsappTemplates(
                atendimento?.currentDepartment?.id ?? null
            )
            if (res.success && res.result) {
                setSendableTemplates(res.result)
            } else {
                toast.error(res.error || 'Erro ao carregar templates')
            }
        } catch (error) {
            console.error('Erro ao carregar templates:', error)
            toast.error('Erro ao carregar templates')
        } finally {
            setLoadingTemplates(false)
        }
    }

    const renderTemplatePreview = (): string => {
        if (!selectedTemplate) return ''
        // Em template tokenizado, mostra o corpo semântico (legível); o backend
        // resolve os valores reais no envio.
        if (isTokenizedTemplate) {
            return (
                selectedTemplate.bodyTemplate || selectedTemplate.bodyText || ''
            )
        }
        let text = selectedTemplate.bodyText || ''
        for (const idx of extractTemplateVars(text)) {
            const value = templateVars[String(idx)] || `{{${idx}}}`
            text = text.replaceAll(`{{${idx}}}`, value)
        }
        return text
    }

    const handleSendTemplate = async () => {
        if (!atendimento || !selectedTemplate) return

        // Template tokenizado: backend resolve as variáveis automaticamente.
        let variables: string[] = []
        if (!isTokenizedTemplate) {
            const indices = extractTemplateVars(selectedTemplate.bodyText || '')
            const missing = indices.filter(
                i => !templateVars[String(i)]?.trim()
            )
            if (missing.length > 0) {
                toast.warn('Preencha todos os campos variáveis do template')
                return
            }
            variables = indices.map(i => templateVars[String(i)].trim())
        }

        setSendingTemplate(true)
        try {
            const res = await sendManualTemplate({
                messageId: atendimento.id,
                // O id local identifica o template em todos os canais. A lista
                // já vem filtrada pelo canal do setor (aprovados na API oficial).
                templateId: selectedTemplate.id,
                variables,
                previewText: renderTemplatePreview(),
            })
            if (res.success) {
                toast.success('Template enviado com sucesso')
                setShowTemplateModal(false)
            } else {
                toast.error(res.error || 'Erro ao enviar template')
            }
        } catch (error: any) {
            console.error('Erro ao enviar template:', error)
            toast.error(
                error?.response?.data?.message || 'Erro ao enviar template'
            )
        } finally {
            setSendingTemplate(false)
        }
    }

    /** `true` quando a mensagem saiu (ou entrou na fila de revisão). */
    const handleSendMessage = async (): Promise<boolean> => {
        if (!atendimento) return false

        const text = inputText.trim()
        const hasFiles = selectedFiles.length > 0

        if (!text && !hasFiles && quickMediaUrls.length === 0) {
            toast.warning('Digite uma mensagem ou anexe um arquivo')
            return false
        }

        try {
            setSending(true)

            // Upload files first if any
            let mediaUrls: string[] = []
            if (hasFiles) {
                mediaUrls = await handleUploadFiles()
                if (mediaUrls.length === 0) {
                    throw new Error('Falha no upload dos arquivos')
                }
            }

            // Anexo da mensagem rápida já está no storage: entra na frente, na
            // ordem em que o autor cadastrou.
            mediaUrls = [...quickMediaUrls, ...mediaUrls]

            // O checkbox some no WhatsApp Web, mas a preferência fica salva no
            // dispositivo: sem esta guarda, o modo revisão continuaria ativo
            // (e invisível) para quem já o tinha ligado.
            if (reviewMode && !isWhatsappWeb) {
                // Adicionar à fila de pendentes com timer de 10 segundos
                addPendingMessage(text, mediaUrls, [...selectedFiles])
                setInputText('')
                setSelectedFiles([])
                setUploadedUrls([])
                setQuickMediaUrls([])
                setSending(false)
                emitScreenEvent({ type: 'chat:sent', chatId: atendimento.id })
                return true
            }

            // Send message
            const response = await sendManualMessage({
                messageId: atendimento.id,
                body: text || undefined,
                mediaUrl: mediaUrls.length > 0 ? mediaUrls : undefined,
                replyToMessageId: replyTo?.id,
            })

            if (response.success) {
                setInputText('')
                setSelectedFiles([])
                setUploadedUrls([])
                setQuickMediaUrls([])
                setReplyTo(null)
                // Modo IA: a mensagem que ele esperava aprovar saiu (ver ai-user-runner).
                emitScreenEvent({ type: 'chat:sent', chatId: atendimento.id })
                // Reload messages
                await loadAtendimento(true)
                return true
            } else {
                toast.error(response.error || 'Erro ao enviar mensagem')
                return false
            }
        } catch (error: any) {
            toast.error(error.message || 'Erro ao enviar mensagem')
            return false
        } finally {
            setSending(false)
        }
    }

    /**
     * Controlador desta tela para o Modo IA (`lib/ai-user/screen-bridge.ts`). A IA
     * escreve no MESMO campo e envia pelo MESMO caminho do botão, à vista do
     * supervisor. As refs evitam registrar de novo a cada tecla digitada.
     */
    const inputTextRef = useRef(inputText)
    inputTextRef.current = inputText
    const handleSendMessageRef = useRef(handleSendMessage)
    handleSendMessageRef.current = handleSendMessage
    const screenChatId = atendimento?.id
    useEffect(() => {
        if (!screenChatId) return
        return registerScreenController('chat', {
            chatId: screenChatId,
            typeDraft: (text, signal) => typeProgressively(text, setInputText, signal),
            attachFiles: files => setSelectedFiles(prev => [...prev, ...files]),
            clearDraft: () => {
                setInputText('')
                setSelectedFiles([])
            },
            getDraft: () => inputTextRef.current,
            send: () => handleSendMessageRef.current(),
        })
    }, [screenChatId])

    /**
     * Carrega as mensagens rápidas uma única vez por chat, no primeiro "/".
     * Buscar na abertura do atendimento seria uma requisição a mais em cada
     * conversa aberta, e a maioria nunca usa o atalho.
     */
    const loadQuickMessages = useCallback(async () => {
        if (quickLoaded || quickLoading) return
        setQuickLoading(true)
        try {
            const [res, flowsRes] = await Promise.all([
                getAvailableQuickMessages(
                    atendimento?.currentDepartment?.id ?? null
                ),
                // Sem lead não há para quem iniciar fluxo: nem busca.
                chatLeadId
                    ? getLaunchableSalesFlows().catch(() => null)
                    : Promise.resolve(null),
            ])
            if (res.success) setQuickMessages(res.result)
            if (flowsRes?.success && Array.isArray(flowsRes.result)) {
                setLaunchableFlows(flowsRes.result)
            }
            setQuickLoaded(true)
        } catch {
            // Sem mensagens rápidas o chat continua funcionando: falha aqui não
            // merece toast em cima de quem só queria digitar uma barra.
            setQuickLoaded(true)
        } finally {
            setQuickLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quickLoaded, quickLoading, atendimento?.currentDepartment?.id, chatLeadId])

    /**
     * Fluxos do "/": fluxo com setor definido só aparece em conversa daquele setor
     * (ele enviaria por outro número).
     */
    const filteredFlows = useMemo(() => {
        const term = quickQuery.trim().toLowerCase()
        const departmentId = atendimento?.currentDepartment?.id ?? null
        return launchableFlows.filter(
            flow =>
                (flow.departmentId == null || flow.departmentId === departmentId) &&
                (!term || flow.name.toLowerCase().includes(term))
        )
    }, [launchableFlows, quickQuery, atendimento?.currentDepartment?.id])

    const loadFlowRuns = useCallback(async () => {
        if (!atendimento?.id || !chatLeadId) {
            setFlowRuns([])
            return
        }
        try {
            const res = await getChatFlowRuns(atendimento.id)
            if (res.success && Array.isArray(res.result)) setFlowRuns(res.result)
        } catch {
            // A faixa é informativa: sem ela a conversa segue normal.
        }
    }, [atendimento?.id, chatLeadId])

    // Recarrega ao abrir a conversa, a cada mensagem nova (um fluxo pode ter andado) e a
    // cada minuto (pausa que venceu sem mensagem nenhuma).
    useEffect(() => {
        loadFlowRuns()
        const timer = setInterval(loadFlowRuns, 60_000)
        return () => clearInterval(timer)
    }, [loadFlowRuns, messages.length])

    const handleSelectFlow = (flow: LaunchableSalesFlow) => {
        setQuickOpen(false)
        setInputText(prev => (isTriggerOnly(prev) ? '' : prev))
        setPendingFlow(flow)
        requestAnimationFrame(() => inputRef.current?.focus())
    }

    const confirmStartFlow = async () => {
        if (!pendingFlow || !atendimento || !chatLeadId || startingFlow) return
        setStartingFlow(true)
        try {
            const res = await startSalesFlowRun(pendingFlow.id, {
                leadId: chatLeadId,
                chatId: atendimento.id,
            })
            if (res.success) {
                toast.success(`Fluxo "${pendingFlow.name}" iniciado`)
                setPendingFlow(null)
                loadFlowRuns()
            } else {
                toast.error(res.error || 'Não foi possível iniciar o fluxo')
            }
        } catch {
            toast.error('Não foi possível iniciar o fluxo')
        } finally {
            setStartingFlow(false)
        }
    }

    const filteredQuickMessages = useMemo(() => {
        const term = quickQuery.trim().toLowerCase()
        if (!term) return quickMessages
        return quickMessages.filter(
            m =>
                m.trigger.toLowerCase().includes(term) ||
                m.title.toLowerCase().includes(term)
        )
    }, [quickMessages, quickQuery])

    /**
     * O "/" só abre a lista como PRIMEIRO caractere da mensagem. Disparar no
     * meio do texto atrapalharia quem digita URL, data ou fração.
     */
    const handleInputChange = (value: string) => {
        setInputText(value)

        const match = /^\/([a-zA-Z0-9_-]*)$/.exec(value)
        if (match) {
            if (!quickOpen) {
                setQuickOpen(true)
                loadQuickMessages()
            }
            setQuickQuery(match[1].toLowerCase())
            setQuickIndex(0)
        } else if (quickOpen) {
            setQuickOpen(false)
        }
    }

    /**
     * Insere o emoji ONDE O CURSOR ESTÁ, não no fim do texto: quem escreveu a
     * frase inteira e voltou para pontuar no meio dela perderia o lugar.
     */
    const insertEmoji = (emoji: string) => {
        const field = inputRef.current
        if (!field) {
            handleInputChange(inputText + emoji)
            return
        }
        const start = field.selectionStart ?? inputText.length
        const end = field.selectionEnd ?? start
        handleInputChange(inputText.slice(0, start) + emoji + inputText.slice(end))
        // Depois do React repintar: devolve o foco ao campo e deixa o cursor
        // DEPOIS do emoji, pronto para continuar digitando.
        requestAnimationFrame(() => {
            const caret = start + emoji.length
            field.focus()
            field.setSelectionRange(caret, caret)
        })
    }

    /** true quando o campo contém só o atalho sendo digitado ("/bom"). */
    const isTriggerOnly = (value: string) => /^\/[a-zA-Z0-9_-]*$/.test(value)

    const handleSelectQuickMessage = async (message: QuickMessage) => {
        if (!atendimento) return
        setQuickOpen(false)

        try {
            const res = await renderQuickMessage(message.id, atendimento.id)
            if (res.success && res.result) {
                setInputText(prev =>
                    // Veio do atalho: o "/bom" digitado dá lugar ao texto. Veio
                    // do botão com algo já escrito: acrescenta, senão apagaria o
                    // que o atendente estava redigindo.
                    isTriggerOnly(prev) || !prev.trim()
                        ? res.result.body
                        : `${prev.trimEnd()}\n${res.result.body}`
                )
                if (res.result.mediaUrls.length > 0) {
                    setQuickMediaUrls(prev => [
                        ...prev,
                        ...res.result.mediaUrls,
                    ])
                }
            } else {
                toast.error(res.error || 'Erro ao carregar a mensagem rápida')
                setInputText(prev => (isTriggerOnly(prev) ? '' : prev))
            }
        } catch {
            toast.error('Falha ao carregar a mensagem rápida')
            setInputText(prev => (isTriggerOnly(prev) ? '' : prev))
        }

        requestAnimationFrame(() => inputRef.current?.focus())
    }

    /** Descoberta: quem não conhece o "/" chega pelo botão. */
    const openQuickMessagesFromButton = () => {
        if (quickOpen) {
            setQuickOpen(false)
            return
        }
        setQuickQuery('')
        setQuickIndex(0)
        setQuickOpen(true)
        loadQuickMessages()
        requestAnimationFrame(() => inputRef.current?.focus())
    }

    /** Enter envia; Shift+Enter quebra linha (padrão de mensageiro). */
    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Com a lista aberta, as setas e o Enter pertencem a ela — senão o
        // Enter enviaria "/bom" como mensagem em vez de escolher o atalho.
        if (quickOpen) {
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setQuickIndex(i =>
                    Math.min(
                        i + 1,
                        filteredQuickMessages.length + filteredFlows.length - 1
                    )
                )
                return
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault()
                setQuickIndex(i => Math.max(i - 1, 0))
                return
            }
            if (e.key === 'Escape') {
                e.preventDefault()
                setQuickOpen(false)
                return
            }
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                const chosen = filteredQuickMessages[quickIndex]
                if (chosen) {
                    handleSelectQuickMessage(chosen)
                } else {
                    const flow =
                        filteredFlows[quickIndex - filteredQuickMessages.length]
                    if (flow) handleSelectFlow(flow)
                }
                return
            }
        }

        // Chip de fluxo com o campo vazio: Enter confirma o início, Esc desiste.
        if (pendingFlow && !inputText.trim()) {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                confirmStartFlow()
                return
            }
            if (e.key === 'Escape') {
                e.preventDefault()
                setPendingFlow(null)
                return
            }
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSendMessage()
        }
    }

    const handleCloseAtendimento = () => {
        if (!atendimento) return
        setShowCloseModal(true)
    }

    const confirmCloseAtendimento = async () => {
        if (!atendimento) return

        setClosing(true)
        try {
            const response = await closeChat(atendimento.id)

            if (response.success) {
                toast.success(
                    'Atendimento fechado com sucesso! Gerando resumo...'
                )
                setShowCloseModal(false)
                // Recarregar atendimento para mostrar o resumo
                await loadAtendimento(true)
            } else {
                toast.error(response.error || 'Erro ao fechar atendimento')
                setShowCloseModal(false)
            }
        } catch (error: any) {
            toast.error(error.message || 'Erro ao fechar atendimento')
            setShowCloseModal(false)
        } finally {
            setClosing(false)
        }
    }

    // Mesma decisão do input: os dois falam da mesma janela.
    const windowOpen = canInteract

    // Excluir só existe no WhatsApp Web: a API oficial não revoga mensagem
    // entregue. Responder (citar) vale nos dois canais de WhatsApp — na API
    // oficial a citação vai pelo `context.message_id`.
    const isWhatsappWeb =
        (atendimento as any)?.whatsappProvider === 'WHATSAPP_WEB'
    const canReply =
        isWhatsappWeb ||
        ((atendimento as any)?.channel ?? 'WHATSAPP') === 'WHATSAPP'

    // Clique na citação: rola até a mensagem original e a destaca por um
    // instante, como no WhatsApp.
    const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null)
    const scrollToMessage = (messageId: number | null | undefined) => {
        const target = messageId
            ? document.getElementById(`chat-message-${messageId}`)
            : null
        if (!target) {
            toast.info('A mensagem original não está nesta conversa.')
            return
        }
        target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setHighlightedMessageId(messageId!)
        window.setTimeout(
            () => setHighlightedMessageId(current => (current === messageId ? null : current)),
            1800
        )
    }

    const handleDeleteMessage = async () => {
        if (!messageToDelete) return
        setDeletingMessage(true)
        try {
            const response = await deleteChatMessage(messageToDelete.id)
            if (response.success) {
                setMessages(prev =>
                    prev.filter(m => m.id !== messageToDelete.id)
                )
                toast.success('Mensagem apagada para todos')
            } else {
                toast.error(response.error || 'Erro ao apagar a mensagem')
            }
        } catch (error: any) {
            toast.error(
                error?.response?.data?.message || 'Erro ao apagar a mensagem'
            )
        } finally {
            setDeletingMessage(false)
            setMessageToDelete(null)
        }
    }

    const handleReopenAtendimento = async () => {
        if (!atendimento) return

        // Fora da janela: reabrir só é possível enviando um template — abre o
        // modal de template pré-preenchido com o contato/departamento do chat.
        if (!windowOpen) {
            setShowReopenModal(true)
            return
        }

        try {
            const response = await reopenChat(atendimento.id)

            if (response.success) {
                toast.success('Atendimento reaberto com sucesso!')
                setViewingMessages(false) // Resetar visualização ao reabrir
                await loadAtendimento(true)
            } else {
                toast.error(response.error || 'Erro ao reabrir atendimento')
            }
        } catch (error: any) {
            toast.error(error.message || 'Erro ao reabrir atendimento')
        }
    }

    const handleMoveDepartment = async (departmentId: number) => {
        if (
            !atendimento ||
            departmentId === atendimento.currentDepartment?.id
        ) {
            setShowDeptMenu(false)
            return
        }
        setMovingDept(true)
        try {
            const response = await moveChatDepartment(
                atendimento.id,
                departmentId
            )
            if (response.success) {
                toast.success('Atendimento movido de departamento')
                setShowDeptMenu(false)
                await loadAtendimento(true)
            } else {
                toast.error(response.error || 'Erro ao mover de departamento')
            }
        } catch (error: any) {
            toast.error(error.message || 'Erro ao mover de departamento')
        } finally {
            setMovingDept(false)
        }
    }

    const toggleUnitMenu = async () => {
        const next = !showUnitMenu
        setShowUnitMenu(next)
        if (next && units.length === 0 && !loadingUnits) {
            setLoadingUnits(true)
            try {
                const res = await getOrganizationUnits()
                if (res.success && res.result) setUnits(res.result)
            } finally {
                setLoadingUnits(false)
            }
        }
    }

    const handleAssignUnit = async (unit: OrganizationUnit) => {
        if (!atendimento || assigningUnit) return
        if (unit.id === atendimento.company?.id) {
            setShowUnitMenu(false)
            return
        }
        setAssigningUnit(true)
        try {
            const res = await assignChatUnit(atendimento.id, unit.id)
            if (res.success) {
                toast.success(
                    `Unidade definida: ${unit.tradingName || unit.name}`
                )
                setShowUnitMenu(false)
                setAtendimento(prev =>
                    prev
                        ? ({
                              ...prev,
                              companyId: unit.id,
                              company: {
                                  id: unit.id,
                                  name: unit.name,
                                  tradingName: unit.tradingName || '',
                                  whatsappNumber:
                                      (prev.company as any)?.whatsappNumber ||
                                      '',
                                  city: unit.city,
                                  state: unit.state,
                              },
                          } as ChatWithItems)
                        : prev
                )
            } else {
                toast.error(res.error || 'Erro ao definir unidade')
            }
        } catch (error: any) {
            toast.error(error.message || 'Erro ao definir unidade')
        } finally {
            setAssigningUnit(false)
        }
    }

    const handleToggleAiPause = async () => {
        if (!atendimento) return

        try {
            const response = await toggleAiOnHold(atendimento.id)

            if (response.success) {
                const newStatus = !atendimento.aiOnHold
                toast.success(
                    newStatus
                        ? 'IA pausada neste atendimento'
                        : 'IA retomada neste atendimento'
                )
                await loadAtendimento(true)
            } else {
                toast.error(response.error || 'Erro ao alternar pausa da IA')
            }
        } catch (error: any) {
            toast.error(error.message || 'Erro ao alternar pausa da IA')
        }
    }

    const activeAiInstructions = getActiveAiInstructions(atendimento)

    // Estado no próprio gatilho: dentro de um menu, a pausa deixaria de ser visível
    // de relance, e era isso que o botão antigo mostrava pela cor.
    const aiTriggerLabel =
        atendimento?.aiEnabled === false
            ? 'IA desativada'
            : atendimento?.aiOnHold
              ? 'IA pausada'
              : 'IA ativa'

    const aiActions: ActionMenuItem[] = [
        atendimento?.aiOnHold
            ? {
                  key: 'resume-ai',
                  label: 'Retomar IA',
                  icon: <MdPlayArrow />,
                  onClick: handleToggleAiPause,
              }
            : {
                  key: 'pause-ai',
                  label: 'Pausar IA',
                  icon: <MdPause />,
                  onClick: handleToggleAiPause,
              },
        {
            key: 'ai-instructions',
            label: activeAiInstructions ? 'Editar instruções' : 'Instruções para a IA',
            icon: <MdPsychology />,
            onClick: () => setShowAiInstructionsModal(true),
        },
    ]

    const isAIMessage = (message: MessageItem) => {
        return message.role === 'assistant'
    }

    const isCompanyMessage = (message: MessageItem) => {
        return message.role === 'company'
    }

    const formatTime = (dateString: string) => {
        const date = new Date(dateString)
        return new Intl.DateTimeFormat('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
        }).format(date)
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
            </div>
        )
    }

    if (!atendimento) {
        return (
            <div className="h-full min-h-[320px] flex items-center justify-center">
                <div className="text-center max-w-md px-4">
                    <p className="text-xl font-semibold text-error">
                        Não foi possível abrir o atendimento
                    </p>
                    <p className="text-sm text-base-content/70 mt-2">
                        {loadError || 'Atendimento não encontrado.'}
                    </p>
                    <button
                        className="btn btn-secondary mt-4"
                        onClick={() =>
                            onBack
                                ? onBack()
                                : navigate(
                                      clientId
                                          ? `/person/clients/${clientId}`
                                          : '/atendimento/chats'
                                  )
                        }
                    >
                        Voltar
                    </button>
                </div>
            </div>
        )
    }

    const chatClientId = (atendimento as any)?.clientId as number | undefined

    // Instagram/Messenger/Chat: o contato é um id do canal. Sem o telefone não há
    // como achar o cadastro, então ele é pedido antes de listar candidatos.
    const metaChannelName =
        atendimento.channel === 'INSTAGRAM'
            ? 'Instagram'
            : atendimento.channel === 'MESSENGER'
              ? 'Messenger'
              : atendimento.channel === 'DEX_TALK'
                ? 'Dex'
                : null
    const needsContactPhone = !!metaChannelName && !atendimento.phoneNumber

    const openLinkModal = async () => {
        if (!atendimento) return
        setShowLinkModal(true)
        setCreatingNewClient(false)
        setContactPhoneInput('')
        if (needsContactPhone) {
            setCandidates([])
            setNewClientName((atendimento.name || '').trim())
            setNewClientCpf('')
            return
        }
        // O nome do contato costuma vir do perfil do WhatsApp; quando não veio,
        // ele é o próprio telefone e não serve de sugestão.
        const contato = (atendimento.name || '').trim()
        const telefone = (atendimento.phoneNumber || '').replace(/\D/g, '')
        setNewClientName(
            contato && contato.replace(/\D/g, '') !== telefone ? contato : ''
        )
        setNewClientCpf('')
        setLoadingCandidates(true)
        try {
            const response = await getChatClientCandidates(atendimento.id)
            if (response.success && response.result) {
                setCandidates(response.result.candidates || [])
            } else {
                toast.error(response.error || 'Erro ao buscar clientes')
            }
        } catch {
            toast.error('Erro ao buscar clientes')
        } finally {
            setLoadingCandidates(false)
        }
    }

    const handleLinkClient = async (clientId: number) => {
        if (!atendimento) return
        setLinkingClientId(clientId)
        try {
            const response = await linkChatClient(atendimento.id, clientId)
            if (response.success) {
                toast.success('Cliente vinculado ao atendimento')
                setShowLinkModal(false)
                loadAtendimento()
            } else {
                toast.error(response.error || 'Erro ao vincular cliente')
            }
        } catch {
            toast.error('Erro ao vincular cliente')
        } finally {
            setLinkingClientId(null)
        }
    }

    /**
     * Grava o telefone do contato do Instagram/Messenger. O backend já liga a
     * conversa ao cliente quando só um cadastro usa o número; quando há vários
     * (ou nenhum), o modal segue para a lista, como numa conversa do WhatsApp.
     */
    const handleSaveContactPhone = async () => {
        if (!atendimento) return

        if (contactPhoneInput.replace(/\D/g, '').length < 10) {
            toast.error('Informe o telefone com DDD')
            return
        }

        setSavingContactPhone(true)
        try {
            const response = await setChatContactPhone(
                atendimento.id,
                contactPhoneInput
            )
            if (!response.success || !response.result) {
                toast.error(response.error || 'Erro ao salvar o telefone')
                return
            }

            if (response.result.clientId) {
                toast.success('Telefone salvo e cliente vinculado à conversa')
                setShowLinkModal(false)
                loadAtendimento()
                return
            }

            toast.success('Telefone salvo')
            loadAtendimento()
            setLoadingCandidates(true)
            const candidatesResponse = await getChatClientCandidates(
                atendimento.id
            )
            setCandidates(
                candidatesResponse.success
                    ? candidatesResponse.result?.candidates || []
                    : []
            )
        } catch {
            toast.error('Erro ao salvar o telefone')
        } finally {
            setSavingContactPhone(false)
            setLoadingCandidates(false)
        }
    }

    /**
     * Cadastra o cliente e já o vincula à conversa. O telefone vem do chat, e
     * não do que o atendente digitar, para o cadastro nascer casando com a
     * conversa que o originou.
     */
    const handleCreateAndLinkClient = async () => {
        if (!atendimento) return

        const name = newClientName.trim()
        if (name.length < 2) {
            toast.error('Informe o nome do cliente')
            return
        }

        setSavingNewClient(true)
        try {
            const created = await createClient({
                name,
                cpf: newClientCpf.replace(/\D/g, '') || undefined,
                whatsAppNumber: atendimento.phoneNumber || undefined,
            } as any)

            if (!created.success || !created.result?.id) {
                toast.error(created.error || 'Erro ao cadastrar cliente')
                return
            }

            const linked = await linkChatClient(
                atendimento.id,
                created.result.id
            )
            if (!linked.success) {
                // O cadastro existe; só o vínculo falhou. Dizer isso evita o
                // atendente cadastrar a mesma pessoa de novo.
                toast.error(
                    'Cliente cadastrado, mas não foi possível vincular à conversa. Tente vincular pela lista.'
                )
                setCreatingNewClient(false)
                openLinkModal()
                return
            }

            toast.success('Cliente cadastrado e vinculado ao atendimento')
            setShowLinkModal(false)
            setCreatingNewClient(false)
            loadAtendimento()
        } catch {
            toast.error('Erro ao cadastrar cliente')
        } finally {
            setSavingNewClient(false)
        }
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="bg-base-100 px-3 sm:px-4 py-2 sm:py-3 shadow-sm flex-shrink-0 relative">
                <div className="flex items-center gap-2 sm:gap-4 justify-between">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <button
                            className="btn btn-ghost btn-sm btn-square flex-shrink-0"
                            onClick={() => {
                                if (onBack) {
                                    onBack()
                                } else {
                                    navigate(
                                        clientId
                                            ? `/person/clients/${clientId}`
                                            : '/atendimento/chats'
                                    )
                                }
                            }}
                        >
                            <MdArrowBack className="text-xl" />
                        </button>

                        {/* O próprio nome/telefone é o gatilho das ações do
                            cliente — o cabeçalho já tem botões demais para
                            ganhar mais um. */}
                        <div className="min-w-0 flex-1">
                            <ActionsMenu
                                align="left"
                                trigger={
                                    <div className="min-w-0">
                                        <h1 className="text-sm sm:text-lg font-bold truncate hover:text-secondary transition-colors flex items-center gap-1">
                                            <span className="truncate">
                                                {atendimento.name}
                                            </span>
                                            <MdExpandMore className="text-base flex-shrink-0 opacity-60" />
                                        </h1>
                                        <p className="text-xs sm:text-sm text-base-content/60 truncate">
                                            <ChatContactLabel
                                                channel={atendimento.channel}
                                                phoneNumber={atendimento.phoneNumber}
                                            />
                                        </p>
                                    </div>
                                }
                                items={[
                                    {
                                        key: 'wallet',
                                        // Com carteira, o menu DIZ qual é: "Atribuir a
                                        // uma carteira" num contato que já tem dona
                                        // levou vendedoras a trocarem sem perceber.
                                        label: currentWalletName
                                            ? `Carteira: ${currentWalletName}`
                                            : 'Atribuir a uma carteira',
                                        icon: <MdAccountBalanceWallet />,
                                        onClick: () => setShowWalletModal(true),
                                        disabled: !chatClientId,
                                        title: !chatClientId
                                            ? 'Converta o contato em cliente para definir a carteira'
                                            : currentWalletName
                                              ? `O contato é da carteira ${currentWalletName}. Clique para trocar — as oportunidades abertas vão junto.`
                                              : 'Colocar o cliente desta conversa numa carteira',
                                    },
                                    ...(!chatClientId
                                        ? [
                                              {
                                                  key: 'link',
                                                  label: 'Vincular a um cliente',
                                                  icon: <MdLink />,
                                                  onClick: openLinkModal,
                                              },
                                          ]
                                        : []),
                                    {
                                        key: 'client',
                                        label: 'Ver cadastro do cliente',
                                        icon: <MdPerson />,
                                        onClick: () =>
                                            navigateOutOfDock(
                                                `/person/clients/${chatClientId}`
                                            ),
                                        disabled: !chatClientId,
                                        title: chatClientId
                                            ? undefined
                                            : 'Esta conversa ainda não tem cliente',
                                    },
                                    ...((atendimento as any).leadId
                                        ? [
                                              {
                                                  key: 'lead',
                                                  label: 'Abrir lead',
                                                  icon: <MdPersonAdd />,
                                                  onClick: () =>
                                                      navigateOutOfDock(
                                                          `/sales/leads/${(atendimento as any).leadId}`
                                                      ),
                                              },
                                          ]
                                        : []),
                                ]}
                            />
                        </div>

                        <ChatReaders chatId={atendimento.id} />

                        {/* Conversa sem cliente não tem carteira, logo não tem
                            dono: fica na fila aberta e some das listas de quem
                            trabalha por carteira. É pendência, e o operador
                            precisa VER isso. */}
                        {!chatClientId && (
                            <button
                                type="button"
                                onClick={openLinkModal}
                                className="btn btn-error btn-xs gap-1 flex-shrink-0 animate-pulse"
                                title="Esta conversa não está vinculada a um cliente — clique para resolver"
                            >
                                <MdPriorityHigh className="text-base" />
                                <span className="hidden sm:inline">
                                    Sem cliente
                                </span>
                            </button>
                        )}

                        {/* Só o estado EXCEPCIONAL merece selo: uma conversa
                            aberta é o normal, e dizê-lo ocupa espaço sem
                            informar nada. */}
                        {atendimento.status !== 'OPEN' && (
                            <div className="badge badge-sm badge-success flex-shrink-0 hidden sm:inline-flex">
                                Fechado
                            </div>
                        )}
                    </div>

                    {/* Desktop Actions */}
                    <div className="hidden lg:flex items-center gap-2">
                        {atendimento.status === 'OPEN' && (
                            <>
                                <ActionsMenu
                                    items={aiActions}
                                    disabled={atendimento.aiEnabled === false}
                                    trigger={
                                        <span
                                            className={`btn btn-sm gap-2 ${
                                                atendimento.aiEnabled === false
                                                    ? 'btn-ghost btn-disabled'
                                                    : atendimento.aiOnHold
                                                      ? 'btn-warning'
                                                      : 'btn-outline'
                                            }`}
                                            title={
                                                atendimento.aiEnabled === false
                                                    ? 'IA desativada globalmente no painel master'
                                                    : undefined
                                            }
                                        >
                                            <MdSmartToy className="text-lg" />
                                            {aiTriggerLabel}
                                            {activeAiInstructions && (
                                                <span
                                                    className="badge badge-primary badge-xs"
                                                    title="Esta conversa tem instruções para a IA"
                                                />
                                            )}
                                            <MdKeyboardArrowDown className="text-lg" />
                                        </span>
                                    }
                                />

                                <button
                                    className="btn btn-sm btn-success"
                                    onClick={handleCloseAtendimento}
                                    title="Fechar atendimento"
                                >
                                    <MdCheckCircle className="text-lg" />
                                    Encerrar
                                </button>
                            </>
                        )}

                        {atendimento.status === 'CLOSED' && viewingMessages && (
                            <button
                                className="btn btn-sm btn-outline"
                                onClick={() => setViewingMessages(false)}
                                title="Voltar ao resumo"
                            >
                                <MdArrowBack className="text-lg" />
                                Voltar ao Resumo
                            </button>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="btn btn-ghost btn-sm btn-square lg:hidden flex-shrink-0"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        <MdMenu className="text-xl" />
                    </button>
                </div>

                {/* Departamento é CONTEXTO da conversa, não ação: linha própria
                    alinhada ao nome, sem disputar espaço com os botões. */}
                <div className="flex flex-wrap items-center gap-2 mt-1.5 pl-10 sm:pl-12">
                    <div className="relative">
                        <button
                            type="button"
                            className="badge badge-secondary badge-sm gap-1 cursor-pointer"
                            onClick={() => setShowDeptMenu(v => !v)}
                            title="Mover de departamento"
                        >
                            <MdBusiness className="text-xs" />
                            {atendimento.currentDepartment?.name ?? 'Sem setor'}
                            <MdExpandMore className="text-xs" />
                        </button>
                        {showDeptMenu && (
                            <div className="absolute z-30 top-full mt-1 left-0 bg-base-100 border border-base-300 rounded-lg shadow-lg w-56 max-h-64 overflow-y-auto">
                                <div className="px-3 py-1.5 text-[11px] font-semibold text-base-content/50 border-b border-base-300/40">
                                    Mover para o departamento
                                </div>
                                {moveDepartments.map(d => (
                                    <button
                                        key={d.id}
                                        type="button"
                                        disabled={movingDept}
                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-base-200 ${
                                            d.id ===
                                            atendimento.currentDepartment?.id
                                                ? 'bg-secondary/10 font-medium'
                                                : ''
                                        }`}
                                        onClick={() =>
                                            handleMoveDepartment(d.id)
                                        }
                                    >
                                        {d.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* Unidade e pasta descem para cá: são CONTEXTO da conversa, como o
                        setor. Em cima, a cidade comia o espaço dos botões de ação. */}
                    <div className="relative flex-shrink-0 unit-menu-container">
                        <button
                            type="button"
                            onClick={toggleUnitMenu}
                            className={`badge badge-sm gap-1 cursor-pointer ${
                                atendimento.company
                                    ? 'badge-ghost'
                                    : 'badge-warning'
                            }`}
                            title={
                                atendimento.company
                                    ? 'Unidade do atendimento'
                                    : 'Definir unidade'
                            }
                        >
                            <MdLocationOn className="text-xs" />
                            <span className="max-w-[120px] truncate">
                                {atendimento.company
                                    ? atendimento.company.city
                                        ? `${atendimento.company.city}${
                                              atendimento.company.state
                                                  ? '/' +
                                                    atendimento.company.state
                                                  : ''
                                          }`
                                        : atendimento.company.tradingName ||
                                          atendimento.company.name
                                    : 'Sem cidade'}
                            </span>
                            <MdExpandMore className="text-xs" />
                        </button>
                        {showUnitMenu && (
                            <div className="absolute z-30 top-full mt-1 right-0 bg-base-100 border border-base-300 rounded-lg shadow-lg w-64 max-h-72 overflow-y-auto">
                                <div className="px-3 py-1.5 text-[11px] font-semibold text-base-content/50 border-b border-base-300/40">
                                    Definir unidade
                                </div>
                                {loadingUnits ? (
                                    <div className="flex justify-center py-4">
                                        <span className="loading loading-spinner loading-sm" />
                                    </div>
                                ) : units.length === 0 ? (
                                    <div className="px-3 py-3 text-sm text-base-content/50">
                                        Nenhuma unidade disponível
                                    </div>
                                ) : (
                                    units.map(u => (
                                        <button
                                            key={u.id}
                                            type="button"
                                            disabled={assigningUnit}
                                            onClick={() => handleAssignUnit(u)}
                                            className={`w-full text-left px-3 py-2 text-sm hover:bg-base-200 flex items-center gap-2 ${
                                                u.id === atendimento.company?.id
                                                    ? 'bg-secondary/10 font-medium'
                                                    : ''
                                            }`}
                                        >
                                            <MdLocationOn className="text-xs text-base-content/40 flex-shrink-0" />
                                            <span className="flex-1 truncate">
                                                {u.tradingName || u.name}
                                                {u.city && (
                                                    <span className="text-base-content/50">
                                                        {' '}
                                                        — {u.city}
                                                        {u.state
                                                            ? '/' + u.state
                                                            : ''}
                                                    </span>
                                                )}
                                            </span>
                                            {u.id ===
                                                atendimento.company?.id && (
                                                <MdCheck className="text-secondary flex-shrink-0" />
                                            )}
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                    </div>

                    {/* Mover para pasta (sempre visível) */}
                    <ChatActionsMenu
                        chatId={atendimento.id}
                        folders={chatFolders}
                        currentFolderId={currentFolderId}
                        onChanged={() => {
                            loadChatFolders()
                            window.dispatchEvent(
                                new CustomEvent('chat-folders-changed')
                            )
                        }}
                        buttonClassName="btn btn-ghost btn-xs btn-square flex-shrink-0"
                        iconClassName="text-base"
                    />
                </div>

                {/* Mobile Menu Dropdown */}
                {mobileMenuOpen && (
                    <div className="lg:hidden mt-3 pt-3 border-t border-base-300/50 space-y-2">
                        {atendimento.status !== 'OPEN' && (
                            <div className="flex items-center justify-between">
                                <div className="badge badge-sm badge-success">
                                    Fechado
                                </div>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2">
                            {atendimento.status === 'OPEN' && (
                                <>
                                    <button
                                        disabled={atendimento.aiEnabled === false}
                                        className={`btn btn-sm flex-1 ${
                                            atendimento.aiEnabled === false
                                                ? 'btn-ghost'
                                                : atendimento.aiOnHold
                                                  ? 'btn-success'
                                                  : 'btn-warning'
                                        }`}
                                        onClick={() => {
                                            handleToggleAiPause()
                                            setMobileMenuOpen(false)
                                        }}
                                        title={
                                            atendimento.aiEnabled === false
                                                ? 'IA desativada globalmente no painel master'
                                                : undefined
                                        }
                                    >
                                        {atendimento.aiEnabled === false ? (
                                            <>
                                                <MdPause className="text-lg" />
                                                IA desativada
                                            </>
                                        ) : atendimento.aiOnHold ? (
                                            <>
                                                <MdPlayArrow className="text-lg" />
                                                Retomar IA
                                            </>
                                        ) : (
                                            <>
                                                <MdPause className="text-lg" />
                                                Pausar IA
                                            </>
                                        )}
                                    </button>

                                    <button
                                        disabled={atendimento.aiEnabled === false}
                                        className="btn btn-sm btn-outline flex-1"
                                        onClick={() => {
                                            setShowAiInstructionsModal(true)
                                            setMobileMenuOpen(false)
                                        }}
                                    >
                                        <MdPsychology className="text-lg" />
                                        Instruções
                                    </button>

                                    <button
                                        className="btn btn-sm btn-success flex-1"
                                        onClick={() => {
                                            handleCloseAtendimento()
                                            setMobileMenuOpen(false)
                                        }}
                                    >
                                        <MdCheckCircle className="text-lg" />
                                        Encerrar
                                    </button>
                                </>
                            )}

                            {atendimento.status === 'CLOSED' &&
                                viewingMessages && (
                                    <button
                                        className="btn btn-sm btn-outline flex-1"
                                        onClick={() => {
                                            setViewingMessages(false)
                                            setMobileMenuOpen(false)
                                        }}
                                    >
                                        <MdArrowBack className="text-lg" />
                                        Voltar ao Resumo
                                    </button>
                                )}
                        </div>
                    </div>
                )}
            </div>

            {/* Messages Area or Summary */}
            {atendimento.status === 'CLOSED' && !viewingMessages ? (
                // Exibir resumo quando fechado
                <div className="flex-1 overflow-y-auto px-4 py-6 bg-base-200/30">
                    <div className="max-w-4xl mx-auto">
                        <div className="card bg-base-100 shadow-xl">
                            <div className="card-body">
                                <div className="flex items-center justify-between mb-4 sm:flex-row flex-col gap-2">
                                    <h2 className="card-title text-2xl">
                                        Como foi o atendimento?
                                    </h2>
                                    <div className="flex gap-2">
                                        <button
                                            className="btn btn-outline btn-sm"
                                            onClick={() =>
                                                setViewingMessages(true)
                                            }
                                        >
                                            <MdChat className="text-lg" />
                                            Mensagens
                                        </button>
                                        <button
                                            className="btn btn-secondary btn-sm"
                                            onClick={handleReopenAtendimento}
                                            title={
                                                windowOpen
                                                    ? 'Reabrir atendimento'
                                                    : 'Reabrir enviando um template'
                                            }
                                        >
                                            <MdPlayArrow className="text-lg" />
                                            {windowOpen
                                                ? 'Reabrir'
                                                : 'Reabrir com template'}
                                        </button>
                                    </div>
                                </div>

                                <div className="divider"></div>

                                {!windowOpen && (
                                    <div className="alert alert-info mb-4">
                                        <MdAccessTime className="text-xl" />
                                        <div className="flex-1">
                                            <div className="text-xs">
                                                A janela de 24h expirou. A
                                                reabertura será feita enviando
                                                um template aprovado.
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {summaries.length > 0 ? (
                                    <div className="space-y-4">
                                        {summaries.map((s, idx) => (
                                            <div
                                                key={s.id}
                                                className="rounded-xl border border-base-300/60 p-4"
                                            >
                                                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                                                    <span className="badge badge-ghost badge-sm font-normal whitespace-nowrap">
                                                        {new Date(
                                                            s.periodStart
                                                        ).toLocaleString(
                                                            'pt-BR'
                                                        )}{' '}
                                                        —{' '}
                                                        {new Date(
                                                            s.periodEnd
                                                        ).toLocaleString(
                                                            'pt-BR'
                                                        )}
                                                    </span>
                                                    <span className="text-xs text-base-content/60">
                                                        {s.messageCount}{' '}
                                                        {s.messageCount === 1
                                                            ? 'mensagem'
                                                            : 'mensagens'}
                                                    </span>
                                                </div>
                                                <div className="prose max-w-none">
                                                    {formatLongSummary(
                                                        s.summaryFull ||
                                                            s.summary ||
                                                            'Resumo indisponível.'
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : atendimento.summaryFull ? (
                                    // Compat: chats antigos com resumo único (antes
                                    // da tabela de resumos por período).
                                    <div className="prose max-w-none">
                                        {formatLongSummary(
                                            atendimento.summaryFull
                                        )}
                                    </div>
                                ) : atendimento.aiEnabled === false ? (
                                    <div className="alert">
                                        <span>
                                            Resumo automático indisponível: a IA
                                            está desativada.
                                        </span>
                                    </div>
                                ) : (
                                    <div className="alert">
                                        <span>
                                            Nenhum resumo gerado ainda. Os
                                            resumos são gerados automaticamente
                                            ao encerrar cada período de
                                            atendimento.
                                        </span>
                                    </div>
                                )}

                                <div className="divider"></div>

                                <div className="flex items-center justify-between text-sm text-base-content/60">
                                    <span>Atendimento #{atendimento.id}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                // Exibir chat quando aberto
                <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4 bg-base-200/30">
                    {messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <p className="text-base-content/60">
                                Nenhuma mensagem ainda
                            </p>
                        </div>
                    ) : (
                        messages
                            .filter(message => {
                                // Ocultar mensagens assistant que são apenas chamadas de tool (sem conteúdo visível)
                                if (
                                    message.role === 'assistant' &&
                                    message.metadata?.tool_calls &&
                                    !message.content
                                )
                                    return false
                                // Ocultar instruções internas do sistema
                                if (
                                    message.role === 'assistant' &&
                                    message.metadata?.type ===
                                        'system_instruction'
                                )
                                    return false
                                return true
                            })
                            .map((message, index, visible) => {
                                const isAI = isAIMessage(message)
                                const isCompany = isCompanyMessage(message)
                                const isClient = message.role === 'user'
                                // Aviso do próprio sistema ("Atendimento transferido
                                // para Fulana por Beltrana"): faixa central discreta,
                                // como o aviso de criptografia do WhatsApp. Não é bolha
                                // de ninguém — não tem autor nem lado.
                                const isSystem = message.role === 'system'

                                // Separador de dia: a bolha mostra só a hora, e
                                // numa conversa longa isso não diz se a mensagem
                                // é de hoje ou da semana passada. Compara com a
                                // anterior VISÍVEL, não com a anterior crua — as
                                // ocultas acima não desenham nada.
                                const dayLabel = startsNewDay(
                                    message.createdAt,
                                    visible[index - 1]?.createdAt
                                )
                                    ? messageDayLabel(message.createdAt)
                                    : null

                                if (isSystem) {
                                    return (
                                        <Fragment key={message.id}>
                                            {dayLabel && (
                                                <div className="sticky top-0 z-20 flex justify-center my-3 pointer-events-none">
                                                    <span className="text-xs font-medium text-base-content/70 bg-base-100/95 backdrop-blur-sm border border-base-300/60 rounded-full px-3 py-1 shadow-sm">
                                                        {dayLabel}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="flex justify-center my-2">
                                                <span className="text-xs text-base-content/60 bg-base-200/70 rounded-full px-3 py-1 max-w-[80%] text-center">
                                                    {message.content}
                                                </span>
                                            </div>
                                        </Fragment>
                                    )
                                }

                                return (
                                    <Fragment key={message.id}>
                                    {dayLabel && (
                                        // `sticky`: a data do grupo fica presa no
                                        // topo enquanto se rola dentro dele. Numa
                                        // conversa longa, sem isso é preciso subir
                                        // até o separador para descobrir de quando
                                        // é a mensagem que se está lendo — que foi
                                        // a reclamação que trouxe o separador.
                                        //
                                        // Funciona porque o separador é filho
                                        // DIRETO do contêiner que rola: cada um
                                        // gruda até o seguinte empurrá-lo, como no
                                        // WhatsApp. Pôr um wrapper entre os dois
                                        // quebra o efeito em silêncio.
                                        <div className="sticky top-0 z-20 flex justify-center my-3 pointer-events-none">
                                            <span className="text-xs font-medium text-base-content/70 bg-base-100/95 backdrop-blur-sm border border-base-300/60 rounded-full px-3 py-1 shadow-sm">
                                                {dayLabel}
                                            </span>
                                        </div>
                                    )}
                                    <div
                                        id={`chat-message-${message.id}`}
                                        className={`flex scroll-mt-16 ${
                                            isClient
                                                ? 'justify-start'
                                                : 'justify-end'
                                        }`}
                                    >
                                        <div
                                            className={`max-w-[70%] md:max-w-[60%] transition-shadow duration-300 ${
                                                highlightedMessageId === message.id
                                                    ? 'ring-4 ring-warning/60'
                                                    : ''
                                            } ${
                                                isClient
                                                    ? 'bg-base-100 border border-base-300'
                                                    : isAI
                                                      ? 'bg-secondary/40 text-black'
                                                      : 'bg-accent/50 text-black'
                                            } rounded-2xl px-4 py-3 shadow-sm group`}
                                        >
                                            {/* Sender Info */}
                                            <div className="flex flex-col gap-1 mb-2">
                                                <div className="flex items-center gap-2">
                                                    {isClient && (
                                                        <MdPerson
                                                            className={`text-lg ${
                                                                isClient
                                                                    ? 'text-secondary'
                                                                    : ''
                                                            }`}
                                                        />
                                                    )}
                                                    {isCompany && (
                                                        <MdPerson2 className="text-lg" />
                                                    )}
                                                    {isAI && (
                                                        <div className="flex items-center gap-1">
                                                            <HiSparkles className="text-lg" />
                                                            <span className="badge badge-xs badge-secondary !text-white">
                                                                IA
                                                            </span>
                                                        </div>
                                                    )}
                                                    <span
                                                        className={`text-xs font-semibold ${
                                                            isClient
                                                                ? 'text-base-content'
                                                                : ''
                                                        }`}
                                                    >
                                                        {isClient
                                                            ? message.name ||
                                                              clientLabel
                                                                  .charAt(0)
                                                                  .toUpperCase() +
                                                                  clientLabel.slice(
                                                                      1
                                                                  )
                                                            : isAI
                                                              ? 'Assistente IA'
                                                              : message.name ||
                                                                'Você'}
                                                    </span>
                                                </div>

                                                {(message.attendant ||
                                                    message.department) && (
                                                    <div className="flex flex-wrap items-center gap-2 text-xs text-base-content/70">
                                                        {isAI &&
                                                            message.attendant && (
                                                                <span className="flex items-center gap-1">
                                                                    <MdPerson className="text-sm" />
                                                                    {
                                                                        message
                                                                            .attendant
                                                                            .name
                                                                    }
                                                                </span>
                                                            )}
                                                        {message.department && (
                                                            <span className="flex items-center gap-1">
                                                                <MdBusiness className="text-sm" />
                                                                {
                                                                    message
                                                                        .department
                                                                        .name
                                                                }
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Citação (mensagem respondida): clicar
                                                rola até a original, como no WhatsApp. */}
                                            {(message as any).metadata?.replyTo
                                                ?.preview && (
                                                <ReplyQuote
                                                    replyTo={(message as any).metadata.replyTo}
                                                    original={messages.find(
                                                        m =>
                                                            m.id ===
                                                            (message as any).metadata.replyTo.messageId
                                                    )}
                                                    clientName={clientLabel}
                                                    onOpen={scrollToMessage}
                                                />
                                            )}

                                            {/* Message Content */}
                                            <div className="text-sm whitespace-pre-wrap break-words">
                                                {renderMessageContent(
                                                    message.content,
                                                    isClient,
                                                    setViewingMedia
                                                )}
                                            </div>

                                            {/* Ações da mensagem: responder nos dois
                                                canais de WhatsApp; apagar só no
                                                WhatsApp Web, o único que apaga de
                                                fato no aparelho do contato. */}
                                            {canReply && (
                                                <div className="flex gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-xs gap-1"
                                                        onClick={() =>
                                                            setReplyTo(message)
                                                        }
                                                        title="Responder esta mensagem"
                                                    >
                                                        <MdReply />
                                                        Responder
                                                    </button>
                                                    {isWhatsappWeb && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-xs gap-1 text-error"
                                                        onClick={() =>
                                                            setMessageToDelete(
                                                                message
                                                            )
                                                        }
                                                        title="Apagar para todos"
                                                    >
                                                        <MdDeleteOutline />
                                                        Excluir
                                                    </button>
                                                    )}
                                                </div>
                                            )}

                                            {/* Time */}
                                            <div className="flex justify-end mt-1">
                                                <span
                                                    className={`text-xs ${
                                                        isClient
                                                            ? 'text-base-content/60'
                                                            : 'text-primary-content/80'
                                                    }`}
                                                >
                                                    {formatTime(
                                                        message.createdAt
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    </Fragment>
                                )
                            })
                    )}
                    {/* Mensagens pendentes (modo revisão) */}
                    {pendingMessages.map(pending => (
                        <div key={pending.id} className="flex justify-end">
                            <div
                                className="max-w-[70%] md:max-w-[60%] bg-warning/15 border-2 border-warning/35 rounded-2xl px-4 py-3 shadow-sm relative cursor-default"
                                onMouseEnter={() =>
                                    pausePendingMessage(pending.id)
                                }
                                onMouseLeave={() =>
                                    resumePendingMessage(pending.id)
                                }
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <MdTimer
                                        className={`text-lg ${pending.paused ? 'text-info' : 'text-warning'}`}
                                    />
                                    <span
                                        className={`text-xs font-semibold ${pending.paused ? 'text-info' : 'text-warning'}`}
                                    >
                                        {`Enviando em ${pending.remainingSeconds}s`}
                                    </span>
                                    <button
                                        onClick={() =>
                                            cancelPendingMessage(pending.id)
                                        }
                                        className="btn btn-ghost btn-xs btn-circle text-error ml-auto"
                                        title="Cancelar envio"
                                    >
                                        <MdDelete className="text-lg" />
                                    </button>
                                </div>
                                <div className="text-sm whitespace-pre-wrap break-words">
                                    {pending.text}
                                </div>
                                {pending.mediaUrls.length > 0 && (
                                    <div className="text-xs text-base-content/60 mt-1">
                                        {pending.mediaUrls.length} arquivo(s)
                                        anexado(s)
                                    </div>
                                )}
                                {/* Progress bar */}
                                <div className="w-full bg-base-300 rounded-full h-1 mt-2">
                                    <div
                                        className={`${pending.paused ? 'bg-info' : 'bg-warning'} h-1 rounded-full transition-all duration-1000`}
                                        style={{
                                            width: `${(pending.remainingSeconds / 10) * 100}%`,
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>
            )}

            {/* Fluxos e agendamentos do lead: também com a conversa fechada, onde roda o
                fluxo disparado pelo encerramento. */}
            {flowRuns.length > 0 && (
                <div className="bg-base-100 border-t border-base-300 px-4 pt-3 flex-shrink-0">
                    <ChatFlowStrip runs={flowRuns} onChanged={loadFlowRuns} />
                </div>
            )}

            {/* Instruções da equipe para a IA: à vista de quem pega a conversa depois,
                com autor e validade — recado escondido num menu é recado esquecido. */}
            {activeAiInstructions && (
                <div className="bg-base-100 border-t border-base-300 px-4 pt-3 flex-shrink-0">
                    <button
                        type="button"
                        className="flex w-full items-start gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-left transition-colors hover:bg-primary/15"
                        onClick={() => setShowAiInstructionsModal(true)}
                        title="Editar instruções para a IA"
                    >
                        <MdPsychology className="mt-0.5 text-lg text-primary shrink-0" />
                        <span className="min-w-0 flex-1">
                            <span className="block text-xs font-medium">
                                Instruções para a IA ({describeAiInstructionsValidity(
                                    activeAiInstructions.mode,
                                    activeAiInstructions.expiresAt
                                )})
                                {activeAiInstructions.userName && (
                                    <span className="font-normal text-base-content/60">
                                        {' '}
                                        · por {activeAiInstructions.userName}
                                    </span>
                                )}
                            </span>
                            <span className="block truncate text-xs text-base-content/70">
                                {activeAiInstructions.text}
                            </span>
                        </span>
                    </button>
                </div>
            )}

            {/* Input Area - Only show when OPEN, or Reopen button when viewing closed messages */}
            {atendimento.status === 'OPEN' ? (
                <div className="bg-base-100 border-t border-base-300 px-4 py-3 flex-shrink-0">
                    {pendingFlow && (
                        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-secondary/40 bg-secondary/10 px-3 py-2">
                            <MdAccountTree className="text-lg text-secondary shrink-0" />
                            <span className="min-w-0 flex-1 text-sm">
                                Iniciar o fluxo{' '}
                                <strong>{pendingFlow.name}</strong> para{' '}
                                <strong>{atendimento.name || 'este lead'}</strong>?
                                <span className="block text-xs text-base-content/60">
                                    Enter confirma, Esc desiste.
                                </span>
                            </span>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => setPendingFlow(null)}
                                disabled={startingFlow}
                            >
                                Desistir
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={confirmStartFlow}
                                disabled={startingFlow}
                            >
                                {startingFlow ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    'Iniciar'
                                )}
                            </button>
                        </div>
                    )}

                    {/* 24h Window Alert */}
                    {!canInteract && (
                        <div className="alert alert-warning mb-3 flex-col sm:flex-row items-start sm:items-center">
                            <div className="flex items-center gap-2 flex-1">
                                <MdAccessTime className="text-xl" />
                                <div className="text-xs">
                                    Só é possível enviar um template aprovado
                                    para reabrir a conversa com o {clientLabel}.
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-warning gap-2"
                                onClick={openTemplateModal}
                            >
                                <MdSend className="text-base" />
                                Enviar
                            </button>
                        </div>
                    )}

                    {/* Barra de gravação (áudio/vídeo) */}
                    {recording && (
                        <div className="mb-3 flex items-center gap-3 rounded-lg border border-base-300 bg-base-200/50 p-2">
                            {recording === 'video' && (
                                <video
                                    ref={videoPreviewRef}
                                    muted
                                    playsInline
                                    className="w-28 h-20 rounded-md bg-black object-cover flex-shrink-0"
                                />
                            )}
                            <span className="relative flex h-3 w-3 flex-shrink-0">
                                <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-error opacity-75" />
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-error" />
                            </span>
                            <span className="font-mono text-sm">
                                {formatRecTime(recordingTime)}
                            </span>
                            <span className="text-sm text-base-content/60 hidden sm:inline">
                                {recording === 'audio'
                                    ? 'Gravando áudio...'
                                    : 'Gravando vídeo...'}
                            </span>
                            <div className="flex-1" />
                            <button
                                type="button"
                                className="btn btn-sm btn-ghost gap-1"
                                onClick={() => stopRecording(true)}
                            >
                                <MdClose className="text-lg" />
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary gap-1"
                                onClick={() => stopRecording(false)}
                            >
                                <MdCheckCircle className="text-lg" />
                                Concluir
                            </button>
                        </div>
                    )}

                    {/* Mensagem sendo respondida */}
                    {replyTo && (
                        <div className="mb-3 flex items-start gap-2 rounded-lg border-l-4 border-secondary bg-base-200/70 px-3 py-2">
                            <MdReply className="text-lg text-secondary mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <span className="block text-xs font-semibold text-base-content/80">
                                    Respondendo a{' '}
                                    {replyTo.role === 'user'
                                        ? replyTo.name || 'contato'
                                        : 'você'}
                                </span>
                                <span className="block text-xs text-base-content/60 truncate">
                                    {replyTo.content}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost btn-xs btn-square"
                                onClick={() => setReplyTo(null)}
                                title="Cancelar resposta"
                            >
                                <MdClose />
                            </button>
                        </div>
                    )}

                    {/* Selected Files Preview */}
                    {selectedFiles.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-2">
                            {selectedFiles.map((file, index) => (
                                <div
                                    key={index}
                                    className="badge badge-lg gap-2 badge-secondary"
                                >
                                    <span className="text-xs truncate max-w-[150px]">
                                        {file.name}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveFile(index)}
                                        className="btn btn-ghost btn-xs btn-circle"
                                    >
                                        <MdClose />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Anexos herdados da mensagem rápida escolhida */}
                    {quickMediaUrls.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-2">
                            {quickMediaUrls.map((url, index) => (
                                <div
                                    key={url}
                                    className="badge badge-lg gap-2 badge-outline"
                                >
                                    <MdBolt className="text-secondary" />
                                    <span className="text-xs truncate max-w-[150px]">
                                        {decodeURIComponent(
                                            url.split('/').pop() || url
                                        )}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setQuickMediaUrls(prev =>
                                                prev.filter(
                                                    (_, i) => i !== index
                                                )
                                            )
                                        }
                                        className="btn btn-ghost btn-xs btn-circle"
                                    >
                                        <MdClose />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Duas faixas: as ferramentas em cima e o campo embaixo. Numa
                        linha só, sete ícones espremiam o campo de escrita — que é o
                        que mais se usa aqui. */}
                    <div className="relative flex flex-col gap-2">
                        {quickOpen && canInteract && (
                            <QuickMessagePicker
                                messages={filteredQuickMessages}
                                flows={filteredFlows}
                                flowsUnavailableReason={
                                    chatLeadId
                                        ? null
                                        : 'Esta conversa não tem lead vinculado: não há para quem iniciar um fluxo.'
                                }
                                activeIndex={quickIndex}
                                loading={quickLoading}
                                query={quickQuery}
                                onSelect={handleSelectQuickMessage}
                                onSelectFlow={handleSelectFlow}
                                onHover={setQuickIndex}
                                onClose={() => setQuickOpen(false)}
                            />
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                            className="hidden"
                            onChange={handleFileSelect}
                        />

                        {/* Faixa de cima: anexos, gravação, atalhos e emoji. */}
                        <div className="flex items-center gap-1 flex-wrap">

                        {/* Desktop Attach Buttons */}
                        <div className="hidden md:flex gap-1">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-square"
                                onClick={() => fileInputRef.current?.click()}
                                title="Anexar arquivo"
                                disabled={!canInteract}
                            >
                                <MdAttachFile className="text-xl" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-square"
                                onClick={() => {
                                    fileInputRef.current?.setAttribute(
                                        'accept',
                                        'image/*'
                                    )
                                    fileInputRef.current?.click()
                                }}
                                title="Enviar imagem"
                                disabled={!canInteract}
                            >
                                <MdImage className="text-xl" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-square"
                                onClick={() => startRecording('audio')}
                                disabled={!canInteract || !!recording}
                                title="Gravar áudio"
                            >
                                <MdMic className="text-xl" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-square"
                                onClick={() => startRecording('video')}
                                disabled={!canInteract || !!recording}
                                title="Gravar vídeo"
                            >
                                <MdVideocam className="text-xl" />
                            </button>
                            <button
                                type="button"
                                className={`btn btn-ghost btn-sm btn-square ${
                                    quickOpen ? 'text-secondary' : ''
                                }`}
                                onClick={openQuickMessagesFromButton}
                                title='Mensagens rápidas e fluxos (digite "/" no campo)'
                                disabled={!canInteract}
                            >
                                <MdBolt className="text-xl" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-square"
                                onClick={() => setShowScheduleModal(true)}
                                title={
                                    chatLeadId
                                        ? 'Agendar envio'
                                        : 'Agendar envio (a conversa precisa ter lead vinculado)'
                                }
                                disabled={!canInteract || !chatLeadId}
                            >
                                <MdScheduleSend className="text-xl" />
                            </button>
                        </div>

                            <EmojiPickerButton
                                onSelect={insertEmoji}
                                disabled={sending || uploading || !canInteract}
                            />
                        </div>

                        {/* Faixa de baixo: o campo de escrita e o envio. */}
                        <div className="flex items-end gap-2">

                        {/* Mobile Attach Button */}
                        <div className="md:hidden relative attach-menu-container">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-square"
                                onClick={() =>
                                    setAttachMenuOpen(!attachMenuOpen)
                                }
                                disabled={!canInteract}
                                title="Anexar arquivo"
                            >
                                <MdAdd className="text-xl" />
                            </button>

                            {/* Mobile Attach Menu */}
                            {attachMenuOpen && (
                                <div className="absolute bottom-full left-0 mb-2 bg-base-100 rounded-lg shadow-lg border border-base-300 p-2 z-50 min-w-[180px]">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm w-full justify-start gap-2"
                                        onClick={() => {
                                            fileInputRef.current?.click()
                                            setAttachMenuOpen(false)
                                        }}
                                    >
                                        <MdAttachFile className="text-xl" />
                                        Anexar arquivo
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm w-full justify-start gap-2"
                                        onClick={() => {
                                            fileInputRef.current?.setAttribute(
                                                'accept',
                                                'image/*'
                                            )
                                            fileInputRef.current?.click()
                                            setAttachMenuOpen(false)
                                        }}
                                    >
                                        <MdImage className="text-xl" />
                                        Enviar imagem
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm w-full justify-start gap-2"
                                        onClick={() => {
                                            startRecording('audio')
                                            setAttachMenuOpen(false)
                                        }}
                                        disabled={!canInteract || !!recording}
                                        title="Gravar áudio"
                                    >
                                        <MdMic className="text-xl" />
                                        Gravar áudio
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm w-full justify-start gap-2"
                                        onClick={() => {
                                            startRecording('video')
                                            setAttachMenuOpen(false)
                                        }}
                                        disabled={!canInteract || !!recording}
                                        title="Gravar vídeo"
                                    >
                                        <MdVideocam className="text-xl" />
                                        Gravar vídeo
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm w-full justify-start gap-2"
                                        onClick={() => {
                                            openQuickMessagesFromButton()
                                            setAttachMenuOpen(false)
                                        }}
                                        disabled={!canInteract}
                                        title="Mensagens rápidas"
                                    >
                                        <MdBolt className="text-xl" />
                                        Mensagens rápidas e fluxos
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm w-full justify-start gap-2"
                                        onClick={() => {
                                            setShowScheduleModal(true)
                                            setAttachMenuOpen(false)
                                        }}
                                        disabled={!canInteract || !chatLeadId}
                                        title="Agendar envio"
                                    >
                                        <MdScheduleSend className="text-xl" />
                                        Agendar envio
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Text Input — cresce com o conteúdo (Shift+Enter quebra linha) */}
                        <textarea
                            ref={inputRef}
                            className="textarea textarea-bordered flex-1 resize-none leading-relaxed"
                            placeholder={
                                canInteract
                                    ? reviewMode
                                        ? 'Modo revisão - 10s para cancelar...'
                                        : 'Digite sua mensagem... (Shift+Enter quebra linha)'
                                    : 'Período de reabertura expirado'
                            }
                            value={inputText}
                            onChange={e => handleInputChange(e.target.value)}
                            onKeyDown={handleKeyPress}
                            disabled={sending || uploading || !canInteract}
                        />

                        {/* Revisar (10s para cancelar) não aparece no WhatsApp
                            Web: o envio ali já passa por fila com intervalo
                            entre mensagens, e somar mais uma espera só atrasa a
                            resposta ao cliente. */}
                        {!isWhatsappWeb && (
                            <label
                                className="flex flex-col items-center gap-1 cursor-pointer select-none flex-shrink-0"
                                title="Revisar antes de enviar (10s para cancelar)"
                            >
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-xs checkbox-warning"
                                    checked={reviewMode}
                                    onChange={e =>
                                        toggleReviewMode(e.target.checked)
                                    }
                                />
                                <span className="text-[10px] text-base-content/50 leading-none">
                                    Revisar
                                </span>
                            </label>
                        )}

                        {/* Send Button */}
                        <button
                            className="btn btn-secondary btn-square flex-shrink-0"
                            onClick={handleSendMessage}
                            disabled={
                                !canInteract ||
                                sending ||
                                uploading ||
                                (!inputText.trim() &&
                                    selectedFiles.length === 0 &&
                                    quickMediaUrls.length === 0)
                            }
                        >
                            {sending || uploading ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                <MdSend className="text-xl" />
                            )}
                        </button>
                        </div>
                    </div>
                </div>
            ) : atendimento.status === 'CLOSED' && viewingMessages ? (
                // Botão de reabrir quando visualizando mensagens de atendimento fechado
                <div className="bg-base-100 border-t border-base-300 px-4 py-4 flex-shrink-0">
                    <div className="flex flex-col items-center justify-center gap-3">
                        {!windowOpen && (
                            <div className="alert alert-info max-w-md">
                                <MdAccessTime className="text-xl" />
                                <div className="flex-1">
                                    <div className="text-xs">
                                        A janela de 24h expirou. A reabertura
                                        será feita enviando um template
                                        aprovado.
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="flex gap-2">
                            <button
                                className="btn btn-outline btn-sm"
                                onClick={() => setViewingMessages(false)}
                            >
                                <MdArrowBack className="text-lg" />
                                Voltar ao Resumo
                            </button>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={handleReopenAtendimento}
                                title={
                                    windowOpen
                                        ? 'Reabrir atendimento'
                                        : 'Reabrir enviando um template'
                                }
                            >
                                <MdPlayArrow className="text-lg" />
                                {windowOpen
                                    ? 'Reabrir Atendimento'
                                    : 'Reabrir com template'}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Vincular a conversa a um cliente (desambiguação de telefone) */}
            {showLinkModal && (
                <dialog className="modal modal-open z-50">
                    <div className="modal-box !max-w-lg">
                        <div className="flex items-center justify-between mb-1">
                            <h3 className="text-lg font-bold">
                                Vincular a um cliente
                            </h3>
                            <button
                                className="btn btn-ghost btn-sm btn-circle"
                                onClick={() => setShowLinkModal(false)}
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>
                        <p className="text-sm text-base-content/60 mb-4">
                            {needsContactPhone
                                ? `Esta conversa veio do ${metaChannelName} e ainda não tem telefone. Informe o telefone do contato: é por ele que o sistema encontra o cadastro de quem já é paciente e junta esta conversa ao histórico do WhatsApp.`
                                : creatingNewClient
                                ? `O cadastro nasce com o telefone ${atendimento.phoneNumber} e já fica vinculado a esta conversa.`
                                : candidates.length > 1
                                  ? `${candidates.length} cadastros usam o telefone ${atendimento.phoneNumber}. Escolha de quem é esta conversa — o sistema não adivinha, porque errar aqui abre o prontuário de outra pessoa.`
                                  : `Nenhum cadastro encontrado para ${atendimento.phoneNumber}.`}
                        </p>

                        {needsContactPhone ? (
                            <div className="form-control w-full">
                                <label
                                    className="label pb-1"
                                    htmlFor="contato-telefone"
                                >
                                    <span className="label-text font-medium">
                                        Telefone do contato
                                    </span>
                                </label>
                                <input
                                    id="contato-telefone"
                                    type="tel"
                                    inputMode="tel"
                                    className="input input-bordered w-full"
                                    placeholder="(77) 99999-9999"
                                    value={contactPhoneInput}
                                    onChange={e =>
                                        setContactPhoneInput(e.target.value)
                                    }
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            handleSaveContactPhone()
                                        }
                                    }}
                                    autoFocus
                                />
                                <p className="text-xs text-base-content/50 mt-1 leading-relaxed">
                                    Com DDD. De preferência o número do
                                    WhatsApp, que é o que costuma estar no
                                    cadastro.
                                </p>
                            </div>
                        ) : creatingNewClient ? (
                            <div className="space-y-4">
                                <div className="form-control w-full">
                                    <label
                                        className="label pb-1"
                                        htmlFor="novo-cliente-nome"
                                    >
                                        <span className="label-text font-medium">
                                            Nome completo
                                        </span>
                                    </label>
                                    <input
                                        id="novo-cliente-nome"
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Nome do paciente"
                                        value={newClientName}
                                        onChange={e =>
                                            setNewClientName(e.target.value)
                                        }
                                        autoFocus
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label
                                        className="label pb-1"
                                        htmlFor="novo-cliente-cpf"
                                    >
                                        <span className="label-text font-medium">
                                            CPF
                                        </span>
                                    </label>
                                    <input
                                        id="novo-cliente-cpf"
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Opcional"
                                        value={newClientCpf}
                                        onChange={e =>
                                            setNewClientCpf(e.target.value)
                                        }
                                    />
                                    <p className="text-xs text-base-content/50 mt-1 leading-relaxed">
                                        Opcional agora, mas é o que distingue as
                                        pessoas que dividem este telefone
                                        depois.
                                    </p>
                                </div>
                            </div>
                        ) : loadingCandidates ? (
                            <div className="flex justify-center py-10">
                                <span className="loading loading-spinner loading-md text-secondary" />
                            </div>
                        ) : candidates.length === 0 ? (
                            <div className="text-center py-8 text-sm text-base-content/50">
                                Use "Cadastrar novo" para criar o cliente aqui
                                mesmo, já vinculado a esta conversa.
                            </div>
                        ) : (
                            <div className="border border-base-300 rounded-lg max-h-80 overflow-y-auto">
                                {candidates.map(candidate => (
                                    <button
                                        key={candidate.id}
                                        type="button"
                                        className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300/50 last:border-b-0 flex items-center justify-between gap-3"
                                        onClick={() =>
                                            handleLinkClient(candidate.id)
                                        }
                                        disabled={linkingClientId !== null}
                                    >
                                        <div className="min-w-0">
                                            <div className="font-medium truncate">
                                                {candidate.name}
                                            </div>
                                            <div className="text-xs text-base-content/60">
                                                {candidate.cpf || 'Sem CPF'}
                                                {candidate.birthDate &&
                                                    ` · ${formatDateOnly(candidate.birthDate)}`}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {candidate.isProspect && (
                                                <span className="badge badge-xs badge-ghost">
                                                    Contato
                                                </span>
                                            )}
                                            {linkingClientId ===
                                                candidate.id && (
                                                <span className="loading loading-spinner loading-xs" />
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="modal-action">
                            {needsContactPhone ? (
                                <>
                                    <button
                                        className="btn btn-ghost"
                                        onClick={() => setShowLinkModal(false)}
                                        disabled={savingContactPhone}
                                    >
                                        Fechar
                                    </button>
                                    <button
                                        className="btn btn-secondary gap-2"
                                        onClick={handleSaveContactPhone}
                                        disabled={savingContactPhone}
                                    >
                                        {savingContactPhone ? (
                                            <span className="loading loading-spinner loading-xs" />
                                        ) : (
                                            <MdLink className="text-base" />
                                        )}
                                        Buscar cadastro
                                    </button>
                                </>
                            ) : creatingNewClient ? (
                                <>
                                    <button
                                        className="btn btn-ghost"
                                        onClick={() =>
                                            setCreatingNewClient(false)
                                        }
                                        disabled={savingNewClient}
                                    >
                                        Voltar
                                    </button>
                                    <button
                                        className="btn btn-secondary gap-2"
                                        onClick={handleCreateAndLinkClient}
                                        disabled={savingNewClient}
                                    >
                                        {savingNewClient ? (
                                            <span className="loading loading-spinner loading-xs" />
                                        ) : (
                                            <MdPersonAdd className="text-base" />
                                        )}
                                        Cadastrar e vincular
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        className="btn btn-ghost"
                                        onClick={() => setShowLinkModal(false)}
                                    >
                                        Fechar
                                    </button>
                                    <button
                                        className="btn btn-secondary gap-2"
                                        onClick={() =>
                                            setCreatingNewClient(true)
                                        }
                                    >
                                        <MdPersonAdd className="text-base" />
                                        Cadastrar novo
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setShowLinkModal(false)}>
                            close
                        </button>
                    </form>
                </dialog>
            )}

            {/* Atribuir o cliente desta conversa a uma carteira */}
            {/* Conversa sem cliente vinculado não tem a quem atribuir carteira. O
                botão já vem desabilitado nesse caso; o guarda aqui evita que uma
                mudança futura no menu chame a API com id inválido. */}
            {chatClientId != null && (
                <WalletAssignModal
                    isOpen={showWalletModal}
                    clientId={chatClientId}
                    clientName={atendimento.name}
                    currentWalletId={currentWalletId}
                    onClose={() => setShowWalletModal(false)}
                    // Sem isto, salvar não mudava nada na tela: o menu seguia
                    // oferecendo "Atribuir a uma carteira" para quem acabara de ganhar
                    // uma, e a ação parecia não ter funcionado. `silent` porque a
                    // conversa já está aberta — recarregar com spinner faria a tela
                    // piscar por causa de uma mudança num campo do cabeçalho.
                    onSaved={() => void loadAtendimento(true)}
                />
            )}

            <FilePreviewModal
                file={viewingMedia}
                onClose={() => setViewingMedia(null)}
            />

            {atendimento && (
                <AiInstructionsModal
                    isOpen={showAiInstructionsModal}
                    chat={atendimento}
                    onClose={() => setShowAiInstructionsModal(false)}
                    onSaved={async () => {
                        setShowAiInstructionsModal(false)
                        await loadAtendimento(true)
                    }}
                />
            )}

            {atendimento && (
                <ScheduleMessageModal
                    isOpen={showScheduleModal}
                    chatId={atendimento.id}
                    initialMessage={isTriggerOnly(inputText) ? '' : inputText}
                    officialApi={!isWhatsappWeb}
                    onClose={() => setShowScheduleModal(false)}
                    onScheduled={() => {
                        setShowScheduleModal(false)
                        setInputText('')
                        loadFlowRuns()
                    }}
                />
            )}

            <ConfirmModal
                isOpen={showCloseModal}
                onClose={() => setShowCloseModal(false)}
                onConfirm={confirmCloseAtendimento}
                title="Fechar Atendimento"
                message="Deseja realmente fechar este atendimento?"
                confirmButtonText="Fechar Atendimento"
                cancelButtonText="Cancelar"
                confirmButtonClass="btn btn-success !border-0 !rounded-lg"
                loading={closing}
            />

            <ConfirmModal
                isOpen={!!messageToDelete}
                onClose={() => setMessageToDelete(null)}
                onConfirm={handleDeleteMessage}
                title="Apagar mensagem"
                message="A mensagem será apagada para todos, sumindo também do WhatsApp do contato. Esta ação não pode ser desfeita."
                confirmButtonText="Apagar para todos"
                cancelButtonText="Cancelar"
                confirmButtonClass="btn btn-error !border-0 !rounded-lg"
                loading={deletingMessage}
            />

            {/* Reabertura fora da janela: envia template e reabre este chat */}
            {atendimento && (
                <StartChatModal
                    isOpen={showReopenModal}
                    onClose={() => setShowReopenModal(false)}
                    mode="reopen"
                    prefill={{
                        phoneNumber: atendimento.phoneNumber,
                        name: atendimento.name,
                        departmentId: atendimento.currentDepartment?.id,
                    }}
                    onStarted={() => {
                        setShowReopenModal(false)
                        setViewingMessages(false)
                        loadAtendimento(true)
                    }}
                />
            )}

            {/* Modal de envio de template (fora da janela de 24h) */}
            {showTemplateModal && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-lg">
                        <h3 className="font-bold text-lg mb-1">
                            Enviar template
                        </h3>
                        <p className="text-xs text-base-content/60 mb-4">
                            Templates aprovados podem ser enviados mesmo após a
                            janela de 24h. Ao responder, o {clientLabel} reabre
                            a conversa.
                        </p>

                        {loadingTemplates ? (
                            <div className="flex justify-center py-8">
                                <span className="loading loading-spinner loading-md text-secondary"></span>
                            </div>
                        ) : sendableTemplates.length === 0 ? (
                            <div className="alert alert-info text-sm">
                                Nenhum template aprovado disponível para envio.
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4">
                                <label className="form-control w-full">
                                    <span className="label-text font-medium mb-1">
                                        Template
                                    </span>
                                    <select
                                        className="select select-bordered select-sm w-full"
                                        value={selectedTemplateId ?? ''}
                                        onChange={e => {
                                            const id = e.target.value
                                                ? parseInt(e.target.value, 10)
                                                : null
                                            setSelectedTemplateId(id)
                                            setTemplateVars({})
                                        }}
                                    >
                                        <option value="">
                                            Selecione um template
                                        </option>
                                        {sendableTemplates.map(t => (
                                            <option key={t.id} value={t.id}>
                                                {t.friendlyName}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                {selectedTemplate && (
                                    <>
                                        {isTokenizedTemplate ? (
                                            <div className="text-xs text-base-content/60 bg-info/10 border border-info/20 rounded-lg p-2">
                                                As variáveis deste template são
                                                preenchidas automaticamente no
                                                envio (nome do cliente,
                                                atendente, empresa, etc.).
                                            </div>
                                        ) : (
                                            extractTemplateVars(
                                                selectedTemplate.bodyText || ''
                                            ).map(idx => (
                                                <label
                                                    key={idx}
                                                    className="form-control w-full"
                                                >
                                                    <span className="label-text text-sm mb-1">
                                                        Campo {idx}
                                                    </span>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm w-full"
                                                        value={
                                                            templateVars[
                                                                String(idx)
                                                            ] ?? ''
                                                        }
                                                        onChange={e =>
                                                            setTemplateVars(
                                                                v => ({
                                                                    ...v,
                                                                    [String(
                                                                        idx
                                                                    )]:
                                                                        e.target
                                                                            .value,
                                                                })
                                                            )
                                                        }
                                                    />
                                                </label>
                                            ))
                                        )}

                                        <div>
                                            <span className="label-text text-sm mb-1 block">
                                                Pré-visualização
                                            </span>
                                            <div className="bg-base-200 rounded-lg p-3 text-sm whitespace-pre-wrap">
                                                {renderTemplatePreview()}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => setShowTemplateModal(false)}
                                disabled={sendingTemplate}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm gap-2"
                                onClick={handleSendTemplate}
                                disabled={sendingTemplate || !selectedTemplate}
                            >
                                {sendingTemplate ? (
                                    <span className="loading loading-spinner loading-xs"></span>
                                ) : (
                                    <MdSend className="text-base" />
                                )}
                                Enviar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

/**
 * Balão "respondendo a": quem escreveu a original e a prévia dela. Com a original
 * nesta conversa (`messageId`), o clique rola até ela; sem ela (mensagem anterior à
 * integração), só mostra o texto que o WhatsApp mandou.
 */
function ReplyQuote({
    replyTo,
    original,
    clientName,
    onOpen,
}: {
    replyTo: { messageId?: number | null; preview: string }
    original?: MessageItem
    clientName: string
    onOpen: (messageId: number | null | undefined) => void
}) {
    const author = original
        ? original.role === 'user'
            ? original.name || clientName.charAt(0).toUpperCase() + clientName.slice(1)
            : original.role === 'assistant'
              ? 'Assistente IA'
              : original.name || 'Você'
        : null
    const clickable = !!replyTo.messageId

    return (
        <button
            type="button"
            disabled={!clickable}
            onClick={() => onOpen(replyTo.messageId)}
            title={clickable ? 'Ir para a mensagem respondida' : undefined}
            className={`mb-2 w-full text-left border-l-4 border-secondary/60 bg-base-200/60 rounded-r px-2 py-1 flex flex-col ${
                clickable ? 'cursor-pointer hover:bg-base-200 transition-colors' : 'cursor-default'
            }`}
        >
            {author && <span className="text-xs font-semibold text-secondary">{author}</span>}
            <span className="text-xs text-base-content/70 line-clamp-2 whitespace-pre-wrap">
                {replyTo.preview}
            </span>
        </button>
    )
}
